/**
 * Product Add-ons Component
 * Handles required add-on selection, validation, and cart integration
 */

(function() {
'use strict';

/**
 * Icon Registry (inlined to avoid ES module caching issues)
 */
const icons = {
  /**
   * Plus icon (commonly used for increment actions)
   * @returns {string} SVG markup
   */
  plus() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.9 4.5c0-.499-.401-.9-.9-.9s-.9.401-.9.9v6.6H4.5c-.499 0-.9.401-.9.9s.401.9.9.9h6.6v6.6c0 .499.401.9.9.9s.9-.401.9-.9v-6.6h6.6c.499 0 .9-.401.9-.9s-.401-.9-.9-.9h-6.6V4.5Z" />
    </svg>`;
  },

  /**
   * Minus icon (commonly used for decrement actions)
   * @returns {string} SVG markup
   */
  minus() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.6 12c0-.499.4-.9.9-.9h15c.498 0 .9.401.9.9s-.402.9-.9.9h-15c-.5 0-.9-.401-.9-.9Z" />
    </svg>`;
  },

  /**
   * Chevron down icon (commonly used for accordions and dropdowns)
   * @returns {string} SVG markup
   */
  chevronDown() {
    return `<svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
    </svg>`;
  },

  /**
   * Arrow right icon (commonly used for navigation)
   * @returns {string} SVG markup
   */
  arrowRight() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M9 6l6 6l-6 6" />
    </svg>`;
  },

  /**
   * Info circle icon (used for tooltip triggers in bespoke options)
   * @returns {string} SVG markup
   */
  infoCircle() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.0004 4.20002C16.3091 4.20002 19.8004 7.69127 19.8004 12C19.8004 16.3088 16.3091 19.8 12.0004 19.8C7.69164 19.8 4.20039 16.3088 4.20039 12C4.20039 7.69127 7.69164 4.20002 12.0004 4.20002ZM12.0004 21.6C17.3029 21.6 21.6004 17.3025 21.6004 12C21.6004 6.69753 17.3029 2.40002 12.0004 2.40002C6.69789 2.40002 2.40039 6.69753 2.40039 12C2.40039 17.3025 6.69789 21.6 12.0004 21.6ZM10.5004 15C10.0016 15 9.60039 15.4013 9.60039 15.9C9.60039 16.3988 10.0016 16.8 10.5004 16.8H13.5004C13.9991 16.8 14.4004 16.3988 14.4004 15.9C14.4004 15.4013 13.9991 15 13.5004 15H13.2004V11.7C13.2004 11.2013 12.7991 10.8 12.3004 10.8H10.5004C10.0016 10.8 9.60039 11.2013 9.60039 11.7C9.60039 12.1988 10.0016 12.6 10.5004 12.6H11.4004V15H10.5004ZM12.0004 9.60002C12.6641 9.60002 13.2004 9.06377 13.2004 8.40002C13.2004 7.73627 12.6641 7.20002 12.0004 7.20002C11.3366 7.20002 10.8004 7.73627 10.8004 8.40002C10.8004 9.06377 11.3366 9.60002 12.0004 9.60002Z" fill="currentColor"/>
    </svg>`;
  },

  /**
   * Checkmark icon (used for custom checkboxes)
   * @returns {string} SVG markup
   */
  check() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <path d="M5 12l5 5L19 7" />
    </svg>`;
  },

  /**
   * Check circle icon (filled circle with white checkmark, used for completion indicators)
   * @returns {string} SVG markup
   */
  checkCircle() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 24C5.37188 24 0 18.6281 0 12C0 5.37188 5.37188 0 12 0C18.6281 0 24 5.37188 24 12C24 18.6281 18.6281 24 12 24ZM17.5312 6.82969C17.0297 6.46406 16.3266 6.57656 15.9609 7.07812L10.3641 14.775L7.92188 12.3328C7.48125 11.8922 6.76875 11.8922 6.33281 12.3328C5.89688 12.7734 5.89219 13.4859 6.33281 13.9219L9.70781 17.2969C9.94219 17.5312 10.2609 17.6484 10.5891 17.625C10.9172 17.6016 11.2172 17.4328 11.4094 17.1656L17.7797 8.4C18.1453 7.89844 18.0328 7.19531 17.5312 6.82969Z" />
    </svg>`;
  },

  /**
   * Close icon (cross/dismiss, used for close buttons)
   * @returns {string} SVG markup
   */
  close() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor">
      <path d="M6.77499 8.45C6.30499 7.98 6.30499 7.22 6.77499 6.755C7.24499 6.29 8.00498 6.285 8.46999 6.755L16.02 14.305L23.57 6.755C24.04 6.285 24.8 6.285 25.265 6.755C25.73 7.225 25.735 7.985 25.265 8.45L17.715 16L25.265 23.55C25.735 24.02 25.735 24.78 25.265 25.245C24.795 25.71 24.035 25.715 23.57 25.245L16.02 17.695L8.46999 25.245C7.99999 25.715 7.23999 25.715 6.77499 25.245C6.30999 24.775 6.30499 24.015 6.77499 23.55L14.325 16L6.77499 8.45Z" />
    </svg>`;
  },

  /**
   * Basket with plus icon (used for add to cart in product addons)
   * @returns {string} SVG markup
   */
  basketPlus() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.40002C12.2475 2.40002 12.4837 2.50127 12.6525 2.68127L18.0525 8.38127L18.0712 8.40002H21C21.6637 8.40002 22.2 8.93627 22.2 9.60002C22.2 10.1438 21.84 10.6013 21.345 10.7513L19.6162 18.5213C19.3725 19.62 18.3975 20.4 17.2725 20.4H6.72374C5.59874 20.4 4.62374 19.62 4.37999 18.5213L2.65499 10.7513C2.15999 10.605 1.79999 10.1438 1.79999 9.60002C1.79999 8.93627 2.33624 8.40002 2.99999 8.40002H5.92874L5.94749 8.38127L11.3475 2.68127C11.5162 2.50127 11.7525 2.40002 12 2.40002ZM12 4.60877L8.40749 8.40002H15.5925L12 4.60877ZM12 10.8C11.5012 10.8 11.1 11.2013 11.1 11.7V13.5H9.29999C8.80124 13.5 8.39999 13.9013 8.39999 14.4C8.39999 14.8988 8.80124 15.3 9.29999 15.3H11.1V17.1C11.1 17.5988 11.5012 18 12 18C12.4987 18 12.9 17.5988 12.9 17.1V15.3H14.7C15.1987 15.3 15.6 14.8988 15.6 14.4C15.6 13.9013 15.1987 13.5 14.7 13.5H12.9V11.7C12.9 11.2013 12.4987 10.8 12 10.8Z" fill="white"/>
    </svg>`;
  }
};

/**
 * Bespoke Utilities (inlined to avoid ES module caching issues)
 */
const UNIT_RATIO = 25.4;
const UNIT_STORAGE_KEY = 'dd-unit-preference';

/**
 * Checks if the current unit preference is imperial (inches)
 * @returns {boolean} True if imperial, false if metric
 */
function isImperial() {
  if (typeof window.isImperialUnit === 'function') {
    return window.isImperialUnit();
  }
  return localStorage.getItem(UNIT_STORAGE_KEY) === 'inches';
}

/**
 * Converts millimeters to inches, formatted to 2 decimal places
 * @param {number} mm - Value in millimeters
 * @returns {string} Formatted value in inches
 */
function toInches(mm) {
  return (mm / UNIT_RATIO).toFixed(2);
}

/**
 * Converts inches to millimeters, rounded to whole number
 * @param {number} inches - Value in inches
 * @returns {number} Value in millimeters (integer)
 */
function toMm(inches) {
  return Math.round(inches * UNIT_RATIO);
}

/**
 * Formats a dimension value with the appropriate unit symbol
 * @param {number} mmValue - Value in millimeters (always stored in mm)
 * @param {boolean} [useImperial] - Override unit preference (defaults to current preference)
 * @returns {string} Formatted value with unit (e.g., "600mm" or "23.62"")
 */
function formatDimension(mmValue, useImperial) {
  const imperial = useImperial !== undefined ? useImperial : isImperial();
  if (imperial) {
    return toInches(mmValue) + '"';
  }
  return mmValue + 'mm';
}

/**
 * Formats a dimension range with the appropriate unit
 * @param {number} minMm - Minimum value in millimeters
 * @param {number} maxMm - Maximum value in millimeters
 * @param {boolean} [useImperial] - Override unit preference (defaults to current preference)
 * @returns {string} Formatted range (e.g., "600 - 1050 mm" or "23.62-41.34"")
 */
function formatDimensionRange(minMm, maxMm, useImperial) {
  const imperial = useImperial !== undefined ? useImperial : isImperial();
  if (imperial) {
    return `${toInches(minMm)}-${toInches(maxMm)}"`;
  }
  return `${minMm} - ${maxMm} mm`;
}

/**
 * Validates a dimension input against its min/max constraints
 * @param {HTMLInputElement} input - The input element with min/max attributes
 * @returns {Object} Validation result object
 */
function getDimensionValidation(input) {
  const value = parseFloat(input.value);
  const min = parseFloat(input.min);
  const max = parseFloat(input.max);
  const hasValue = input.value !== '' && !isNaN(value);

  const isBelowMin = hasValue && value < min;
  const isAboveMax = hasValue && value > max;

  return {
    value,
    hasValue,
    isBelowMin,
    isAboveMax,
    isOutOfRange: isBelowMin || isAboveMax,
    isValid: !hasValue || (!isBelowMin && !isAboveMax),
  };
}

/**
 * Generates an error message for dimension validation
 * @param {Object} validation - Result from getDimensionValidation
 * @param {number} minMm - Original minimum in millimeters
 * @param {number} maxMm - Original maximum in millimeters
 * @param {Object} translations - Translation strings
 * @returns {string|null} Error message or null if valid
 */
function getDimensionErrorMessage(validation, minMm, maxMm, translations) {
  if (!validation.isOutOfRange) {
    return null;
  }

  if (validation.isBelowMin) {
    return `${translations.minError} ${formatDimension(minMm)}`;
  }

  if (validation.isAboveMax) {
    return `${translations.maxError} ${formatDimension(maxMm)}`;
  }

  return null;
}

/**
 * Parses dimension data attributes from an element
 * @param {HTMLElement} element - Element with data-min-mm and data-max-mm attributes
 * @returns {Object} Parsed values with minMm, maxMm, and valid properties
 */
function parseDimensionData(element) {
  const minMm = parseFloat(element.dataset.minMm);
  const maxMm = parseFloat(element.dataset.maxMm);

  return {
    minMm,
    maxMm,
    valid: !isNaN(minMm) && !isNaN(maxMm),
  };
}

/**
 * Updates an input element's min/max attributes for unit conversion
 * @param {HTMLInputElement} input - The input element
 * @param {number} minMm - Original minimum in millimeters
 * @param {number} maxMm - Original maximum in millimeters
 * @param {boolean} toImperialUnit - Whether to convert to imperial
 */
function updateInputMinMax(input, minMm, maxMm, toImperialUnit) {
  if (toImperialUnit) {
    input.min = toInches(minMm);
    input.max = toInches(maxMm);
  } else {
    input.min = minMm;
    input.max = maxMm;
  }
}

/**
 * Converts an input value between units
 * @param {HTMLInputElement} input - The input element
 * @param {boolean} toImperialUnit - Whether to convert to imperial
 */
function convertInputValue(input, toImperialUnit) {
  const currentValue = parseFloat(input.value);

  if (isNaN(currentValue) || currentValue <= 0) {
    return;
  }

  if (toImperialUnit) {
    input.value = toInches(currentValue);
  } else {
    input.value = toMm(currentValue);
  }
}

/**
 * Bespoke Custom Select (inlined to avoid ES module caching issues)
 */
const bespokeDesktopQuery = window.matchMedia('(min-width: 750px)');
const MEASUREMENT_PATTERN = /((?:\d+(?:x)\d+(?:x)\d+mm)|(?:(?:\d+(?:x|-)|)\d+mm))(?=[^-"]|$)/gi;

/**
 * Converts a measurement string from mm to inches
 * @param {string} metricStr - String like "40mm" or "100x200mm"
 * @returns {string} Imperial string like "1.57"" or "3.94x7.87""
 */
function convertToImperial(metricStr) {
  const cleanValue = metricStr.replace('mm', '');

  if (cleanValue.includes('x')) {
    const parts = cleanValue.split('x');
    const converted = parts.map(part => toInches(parseFloat(part)));
    return converted.join('x') + '"';
  }

  if (cleanValue.includes('-')) {
    const parts = cleanValue.split('-');
    const converted = parts.map(part => toInches(parseFloat(part)));
    return converted.join('-') + '"';
  }

  return toInches(parseFloat(cleanValue)) + '"';
}

/**
 * Converts measurement labels based on current unit preference
 * @param {string} label - Original label text
 * @returns {string} Converted label
 */
function convertLabel(label) {
  if (!isImperial()) return label;
  return label.replace(MEASUREMENT_PATTERN, (match) => convertToImperial(match));
}

/**
 * Creates a bespoke dropdown field with optional custom select for desktop.
 * @param {Object} dropdown - Dropdown config object
 * @param {Object} options - Configuration options
 * @returns {Object} Object with container, select, and refresh function
 */
function createBespokeDropdown(dropdown, options) {
  const {
    formatMoney,
    renderTooltip,
    placeholderText = 'Please choose...'
  } = options;

  const container = document.createElement('div');
  container.className = 'product-bespoke-dropdown';
  container.dataset.dropdownId = dropdown.id;

  const labelRow = document.createElement('div');
  labelRow.className = 'product-bespoke-dropdown__label-row';

  const label = document.createElement('span');
  label.className = 'product-bespoke-dropdown__label';
  label.textContent = dropdown.label;
  labelRow.appendChild(label);

  if (dropdown.helpText && renderTooltip) {
    labelRow.appendChild(renderTooltip(dropdown.helpText));
  }

  container.appendChild(labelRow);

  const select = buildBespokeNativeSelect(dropdown, formatMoney, placeholderText);
  container.appendChild(select);

  let refresh = () => {};

  if (bespokeDesktopQuery.matches) {
    const customSelect = buildBespokeCustomSelect(dropdown, select, formatMoney, placeholderText);
    container.appendChild(customSelect);
    select.classList.add('bespoke-custom-select-active');
    select.setAttribute('tabindex', '-1');

    refresh = () => syncBespokeCustomFromNative(select, customSelect, dropdown);
  }

  return { container, select, refresh };
}

/**
 * Builds the native select element
 */
function buildBespokeNativeSelect(dropdown, formatMoney, placeholderText) {
  const select = document.createElement('select');
  select.className = 'product-bespoke-dropdown__select';
  select.dataset.dropdownId = dropdown.id;
  select.dataset.label = dropdown.label;
  select.setAttribute('aria-label', dropdown.label);

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = placeholderText;
  placeholder.disabled = true;
  placeholder.selected = true;
  select.appendChild(placeholder);

  dropdown.values.forEach(value => {
    const option = document.createElement('option');
    option.value = value.id;
    option.dataset.label = value.label;
    option.dataset.labelOriginal = value.label;
    option.dataset.priceAdjustment = value.priceAdjustment || 0;
    if (value.swatch) {
      option.dataset.swatch = value.swatch;
    }

    const displayLabel = convertLabel(value.label);
    let text = displayLabel;
    if (value.priceAdjustment > 0) {
      text += ` (+${formatMoney(value.priceAdjustment)})`;
    }
    option.textContent = text;
    select.appendChild(option);
  });

  return select;
}

/**
 * Builds the custom ARIA combobox overlay for desktop
 */
function buildBespokeCustomSelect(dropdown, select, formatMoney, placeholderText) {
  const wrapper = document.createElement('div');
  wrapper.className = 'bespoke-custom-select';

  const listboxId = `bespoke-listbox-${dropdown.id}-${Date.now()}`;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'bespoke-custom-select__trigger';
  trigger.setAttribute('role', 'combobox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-controls', listboxId);
  trigger.setAttribute('aria-label', dropdown.label);

  const triggerContent = document.createElement('span');
  triggerContent.className = 'bespoke-custom-select__trigger-content';

  const valueSpan = document.createElement('span');
  valueSpan.className = 'bespoke-custom-select__value';
  valueSpan.textContent = placeholderText;

  triggerContent.appendChild(valueSpan);

  const iconSpan = document.createElement('span');
  iconSpan.className = 'bespoke-custom-select__icon';
  iconSpan.innerHTML = icons.chevronDown();

  trigger.appendChild(triggerContent);
  trigger.appendChild(iconSpan);

  const listbox = document.createElement('ul');
  listbox.className = 'bespoke-custom-select__listbox';
  listbox.id = listboxId;
  listbox.setAttribute('role', 'listbox');
  listbox.setAttribute('tabindex', '-1');

  dropdown.values.forEach(value => {
    const li = document.createElement('li');
    li.className = 'bespoke-custom-select__option';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.dataset.value = value.id;
    li.dataset.labelOriginal = value.label;
    li.dataset.priceAdjustment = value.priceAdjustment || 0;

    if (value.swatch) {
      const img = document.createElement('img');
      img.className = 'bespoke-custom-select__swatch';
      img.src = value.swatch;
      img.alt = '';
      img.width = 24;
      img.height = 24;
      img.loading = 'lazy';
      li.appendChild(img);
    }

    const optionLabel = document.createElement('span');
    optionLabel.className = 'bespoke-custom-select__option-label';

    const displayLabel = convertLabel(value.label);
    let text = displayLabel;
    if (value.priceAdjustment > 0) {
      text += ` (+${formatMoney(value.priceAdjustment)})`;
    }
    optionLabel.textContent = text;
    li.appendChild(optionLabel);

    listbox.appendChild(li);
  });

  wrapper.appendChild(trigger);
  wrapper.appendChild(listbox);

  attachBespokeBehaviors(wrapper, trigger, listbox, select, dropdown, placeholderText);

  return wrapper;
}

/**
 * Attaches open/close, selection, keyboard, and outside-click behaviors
 */
function attachBespokeBehaviors(wrapper, trigger, listbox, select, dropdown, placeholderText) {
  let focusedIndex = -1;

  function open() {
    wrapper.classList.add('bespoke-custom-select--open');
    trigger.setAttribute('aria-expanded', 'true');

    const selectedOption = listbox.querySelector('[aria-selected="true"]');
    if (selectedOption) {
      focusedIndex = Array.from(listbox.children).indexOf(selectedOption);
      selectedOption.classList.add('bespoke-custom-select__option--focused');
    } else {
      focusedIndex = -1;
    }

    listbox.focus();
  }

  function close() {
    wrapper.classList.remove('bespoke-custom-select--open');
    trigger.setAttribute('aria-expanded', 'false');
    clearFocus();
    trigger.focus();
  }

  function clearFocus() {
    listbox.querySelectorAll('.bespoke-custom-select__option--focused').forEach(el => {
      el.classList.remove('bespoke-custom-select__option--focused');
    });
  }

  function focusOption(index) {
    const items = listbox.children;
    if (index < 0 || index >= items.length) return;

    clearFocus();
    focusedIndex = index;
    items[focusedIndex].classList.add('bespoke-custom-select__option--focused');
    items[focusedIndex].scrollIntoView({ block: 'nearest' });
  }

  function selectValue(valueId) {
    select.value = valueId;
    select.classList.add('has-value');
    select.dispatchEvent(new Event('change', { bubbles: true }));

    syncBespokeCustomFromNative(select, wrapper, dropdown);
    close();
  }

  function isOpen() {
    return wrapper.classList.contains('bespoke-custom-select--open');
  }

  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    if (isOpen()) {
      close();
    } else {
      open();
    }
  });

  listbox.addEventListener('click', (e) => {
    const option = e.target.closest('.bespoke-custom-select__option');
    if (!option) return;
    selectValue(option.dataset.value);
  });

  listbox.addEventListener('keydown', (e) => {
    const items = listbox.children;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusOption(Math.min(focusedIndex + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusOption(Math.max(focusedIndex - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < items.length) {
          selectValue(items[focusedIndex].dataset.value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
    }
  });

  trigger.addEventListener('keydown', (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isOpen()) {
      e.preventDefault();
      open();
    }
  });

  document.addEventListener('click', (e) => {
    if (isOpen() && !wrapper.contains(e.target)) {
      close();
    }
  });
}

/**
 * Syncs the custom dropdown visual state from the native select's current value
 */
function syncBespokeCustomFromNative(select, customWrapper, dropdown) {
  const triggerContent = customWrapper.querySelector('.bespoke-custom-select__trigger-content');
  const valueSpan = triggerContent?.querySelector('.bespoke-custom-select__value');
  const options = customWrapper.querySelectorAll('.bespoke-custom-select__option');

  if (!valueSpan) return;

  const existingSwatch = triggerContent.querySelector('.bespoke-custom-select__trigger-swatch');
  if (existingSwatch) existingSwatch.remove();

  options.forEach(opt => opt.setAttribute('aria-selected', 'false'));

  if (!select.value) {
    valueSpan.textContent = select.options[0]?.textContent || 'Please choose...';
    triggerContent.classList.remove('bespoke-custom-select__trigger-content--has-value');
    return;
  }

  const selectedNativeOption = select.options[select.selectedIndex];
  const selectedCustomOption = customWrapper.querySelector(
    `.bespoke-custom-select__option[data-value="${select.value}"]`
  );

  if (selectedCustomOption) {
    selectedCustomOption.setAttribute('aria-selected', 'true');
  }

  valueSpan.textContent = selectedNativeOption?.textContent || select.value;
  triggerContent.classList.add('bespoke-custom-select__trigger-content--has-value');

  const matchedValue = dropdown.values.find(v => v.id === select.value);
  if (matchedValue?.swatch) {
    const swatchImg = document.createElement('img');
    swatchImg.className = 'bespoke-custom-select__trigger-swatch';
    swatchImg.src = matchedValue.swatch;
    swatchImg.alt = '';
    swatchImg.width = 24;
    swatchImg.height = 24;
    triggerContent.insertBefore(swatchImg, valueSpan);
  }
}

/**
 * Refreshes all custom dropdowns within a given root element.
 * @param {HTMLElement} root - Parent element containing bespoke dropdowns
 * @param {Array} [dropdownConfigs] - Array of dropdown config objects for swatch data
 */
function refreshCustomDropdowns(root, dropdownConfigs) {
  if (!root) return;

  const customSelects = root.querySelectorAll('.bespoke-custom-select');
  customSelects.forEach(customWrapper => {
    const container = customWrapper.closest('.product-bespoke-dropdown');
    if (!container) return;

    const select = container.querySelector('.product-bespoke-dropdown__select');
    if (!select) return;

    const dropdownId = select.dataset.dropdownId;
    const dropdown = dropdownConfigs?.find(d => d.id === dropdownId) || { values: [] };

    syncBespokeCustomFromNative(select, customWrapper, dropdown);
  });
}

/**
 * Updates dropdown labels when unit preference changes.
 * @param {HTMLElement} root - Parent element containing bespoke dropdowns
 * @param {Function} formatMoney - Money formatter function
 */
function updateDropdownUnits(root, formatMoney) {
  if (!root) return;

  const nativeSelects = root.querySelectorAll('.product-bespoke-dropdown__select');
  nativeSelects.forEach(select => {
    Array.from(select.options).forEach(option => {
      if (!option.dataset.labelOriginal) return;

      const displayLabel = convertLabel(option.dataset.labelOriginal);
      const priceAdjustment = parseInt(option.dataset.priceAdjustment) || 0;

      let text = displayLabel;
      if (priceAdjustment > 0 && formatMoney) {
        text += ` (+${formatMoney(priceAdjustment)})`;
      }
      option.textContent = text;
    });
  });

  const customOptions = root.querySelectorAll('.bespoke-custom-select__option');
  customOptions.forEach(option => {
    if (!option.dataset.labelOriginal) return;

    const labelSpan = option.querySelector('.bespoke-custom-select__option-label');
    if (!labelSpan) return;

    const displayLabel = convertLabel(option.dataset.labelOriginal);
    const priceAdjustment = parseInt(option.dataset.priceAdjustment) || 0;

    let text = displayLabel;
    if (priceAdjustment > 0 && formatMoney) {
      text += ` (+${formatMoney(priceAdjustment)})`;
    }
    labelSpan.textContent = text;
  });

  const triggerValues = root.querySelectorAll('.bespoke-custom-select__value');
  triggerValues.forEach(valueSpan => {
    const customWrapper = valueSpan.closest('.bespoke-custom-select');
    const container = customWrapper?.closest('.product-bespoke-dropdown');
    const select = container?.querySelector('.product-bespoke-dropdown__select');

    if (select && select.value) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption) {
        valueSpan.textContent = selectedOption.textContent;
      }
    }
  });
}

class ProductAddons extends HTMLElement {
  constructor() {
    super();
    this.variantSelected = false;
    this.mainVariantId = null;
    this.mainQuantity = 1;
    this.selections = new Map();
    this.compatibilityRules = this.loadCompatibilityRules();
    this.drawer = null;
  }

  connectedCallback() {
    this.isRequiredComponent = this.classList.contains('product-addons--required');
    this.setupVariantListener();
    this.checkInitialVariant();
    this.setupCompatibilityListeners();
    this.setupCategoryButtons();
    this.setupQuantityControls();
    this.setupAddToCartInterception();
    this.loadSavedSelections();
    this.loadEditBundle();
  }

  /**
   * Lazily finds the associated drawer element
   * @returns {ProductAddonsDrawer|null} The drawer element
   */
  getDrawer() {
    if (!this.drawer) {
      const drawerId = this.dataset.drawerId || 'default';
      this.drawer = document.getElementById(`ProductAddonsDrawer-${drawerId}`);
    }
    return this.drawer;
  }

  /**
   * Sets up add-to-cart validation interception
   */
  setupAddToCartInterception() {
    const productVariants = document.querySelector('product-variants');
    if (!productVariants) return;

    const atcButton = productVariants.querySelector('[data-add-to-cart]');
    if (!atcButton) return;

    if (this.isRequiredComponent) {
      atcButton.addEventListener('click', (event) => {
        const isValid = this.validateRequiredAddons();
        if (!isValid && this.variantSelected) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showValidationError();
        }
      }, true);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-variants' && this.selections.size > 0) {
          this.addAddonsToCart(event.groupId);
        }
      });
    }
  }

  /**
   * Shows validation error message
   */
  showValidationError() {
    const message = this.querySelector('.product-addons__message');
    if (!message) return;

    const originalText = message.textContent;
    message.textContent = 'Please select all required add-ons before adding to cart.';
    message.style.color = 'rgba(var(--color-error))';
    setTimeout(() => {
      message.textContent = originalText;
      message.style.color = '';
    }, 3000);
  }

  /**
   * Loads compatibility rules (hardcoded from old theme)
   * @returns {Object} Compatibility rules object
   */
  loadCompatibilityRules() {
    return {
      'exterior-fitted-doors-and-frames': {
        hides: [
          'external-door-frames',
          'draught-excluders',
          'external-door-handle-packs',
          'handles',
          'hinges',
          'locks-latches'
        ],
        displayName: 'External Door and Frame Sets with Fittings'
      },
      'external-door-frames': {
        hides: ['exterior-fitted-doors-and-frames'],
        displayName: 'External Door Frames'
      }
    };
  }

  /**
   * Listens for variant selection events from product form
   */
  setupVariantListener() {
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail);
    });

    document.addEventListener('quantity:change', (event) => {
      const newQuantity = event.detail.totalQuantity || 1;
      const quantityChanged = this.mainQuantity !== newQuantity;
      this.mainQuantity = newQuantity;

      if (quantityChanged && this.isRequiredComponent) {
        this.resetRequiredSelections();
      }

      this.updateAllCategoryDisplays();
      this.validateRequiredAddons();
    });
  }

  /**
   * Checks if any variants have quantity > 0 on page load
   */
  checkInitialVariant() {
    const inputs = document.querySelectorAll('.product-variants__qty-input');
    let hasSelection = false;
    let totalQuantity = 0;

    inputs.forEach(input => {
      const qty = parseInt(input.value) || 0;
      if (qty > 0) {
        hasSelection = true;
        totalQuantity += qty;
      }
    });

    if (hasSelection) {
      this.mainQuantity = totalQuantity;
      this.enableAddons();
    }
  }

  /**
   * Handles variant quantity change from multi-variant selector
   * @param {Object} variantData - Data with hasSelection and totalQuantity
   */
  handleVariantChange(variantData) {
    if (variantData && variantData.hasSelection) {
      const newQuantity = variantData.totalQuantity || 1;
      const quantityChanged = this.mainQuantity !== newQuantity;
      this.mainQuantity = newQuantity;

      if (quantityChanged && this.isRequiredComponent) {
        this.resetRequiredSelections();
      }

      this.enableAddons();
    } else {
      this.disableAddons();
    }
  }

  /**
   * Enables add-ons section after variant selection
   */
  enableAddons() {
    this.variantSelected = true;
    this.classList.remove('product-addons--disabled');

    const disabledMsg = this.querySelector('[data-disabled-message]');
    if (disabledMsg) disabledMsg.hidden = true;

    const buttons = this.querySelectorAll('.product-addon-category__select, .product-addon-category__change');
    buttons.forEach(btn => btn.disabled = false);

    const requiredCategories = this.querySelectorAll('[data-required="true"]');
    requiredCategories.forEach(category => {
      const categoryId = category.dataset.categoryId;
      const quantityCounter = category.querySelector('.product-addon-category__quantity-counter');
      const checkmark = category.querySelector('.product-addon-category__checkmark');
      const currentQuantity = this.getTotalQuantityForCategory(categoryId);
      const isComplete = currentQuantity === this.mainQuantity;

      if (quantityCounter) {
        const totalSpan = quantityCounter.querySelector('.product-addon-category__quantity-total');
        const currentSpan = quantityCounter.querySelector('.product-addon-category__quantity-current');

        if (totalSpan) totalSpan.textContent = this.mainQuantity;
        if (currentSpan) currentSpan.textContent = currentQuantity;

        quantityCounter.removeAttribute('hidden');
      }

      this.updateCompletionIndicators(quantityCounter, checkmark, isComplete);
    });

    this.validateRequiredAddons();
  }

  /**
   * Disables add-ons section when no variant selected
   */
  disableAddons() {
    this.variantSelected = false;
    this.classList.add('product-addons--disabled');

    const buttons = this.querySelectorAll('.product-addon-category__select, .product-addon-category__change');
    buttons.forEach(btn => btn.disabled = true);

    const quantityCounters = this.querySelectorAll('.product-addon-category__quantity-counter');
    const checkmarks = this.querySelectorAll('.product-addon-category__checkmark');

    quantityCounters.forEach(counter => counter.setAttribute('hidden', ''));
    checkmarks.forEach(checkmark => checkmark.setAttribute('hidden', ''));

    const categoryIds = Array.from(this.selections.keys());
    categoryIds.forEach(categoryId => {
      this.clearSelection(categoryId);
    });

    /* Reset drawer loaded state so next open gets a fresh render */
    const drawer = this.getDrawer();
    if (drawer) {
      drawer.loadedCategoryId = null;
    }

    this.toggleAddToCart(false);
  }

  /**
   * Sets up click handlers for category Select/Change buttons
   */
  setupCategoryButtons() {
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.product-addon-category__select, .product-addon-category__change');
      if (button && !button.disabled) {
        const categoryId = button.dataset.categoryId;
        this.openDrawer(categoryId);
        return;
      }

      /* Allow clicking anywhere on the category card to open the drawer */
      const category = event.target.closest('.product-addon-category');
      if (category) {
        /* Skip if click was on a quantity control or variant display */
        if (event.target.closest('[data-action], .product-addon-variant__qty, .product-addon-variant')) return;

        /* Show disabled message if variant not selected (mobile feedback) */
        if (!this.variantSelected) {
          this.showDisabledMessage();
          return;
        }

        const selectBtn = category.querySelector('.product-addon-category__select, .product-addon-category__change');
        if (selectBtn && !selectBtn.disabled) {
          this.openDrawer(category.dataset.categoryId);
        }
      }
    });
  }

  /**
   * Shows the disabled message when tapping add-ons before selecting a variant
   */
  showDisabledMessage() {
    const message = this.querySelector('[data-disabled-message]');
    if (!message) return;

    message.hidden = false;
    message.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Opens the drawer for a specific category
   * @param {String} categoryId - Category to display
   */
  openDrawer(categoryId) {
    const drawer = this.getDrawer();
    if (drawer) {
      drawer.open(categoryId, this);
    }
  }

  /**
   * Calculates total quantity across all selections in a category
   * @param {String} categoryId - Category ID
   * @returns {Number} Total quantity
   */
  getTotalQuantityForCategory(categoryId) {
    const selections = this.selections.get(categoryId);
    if (!selections) return 0;
    return Array.from(selections.values())
      .reduce((sum, sel) => sum + sel.quantity, 0);
  }

  /**
   * Updates completion indicators based on requirement status
   * @param {HTMLElement|null} counter - Counter element
   * @param {HTMLElement|null} checkmark - Checkmark element
   * @param {Boolean} isComplete - Whether requirement is met
   */
  updateCompletionIndicators(counter, checkmark, isComplete) {
    if (!counter && !checkmark) return;

    if (isComplete) {
      counter?.setAttribute('hidden', '');
      checkmark?.removeAttribute('hidden');
    } else {
      counter?.removeAttribute('hidden');
      checkmark?.setAttribute('hidden', '');
    }
  }

  /**
   * Handles product selection from drawer
   * Supports multiple products per category
   * @param {Object} selectionData - Selected product data
   */
  handleProductSelection(selectionData) {
    const { categoryId, variantId, productData, bespokeOptions } = selectionData;

    if (!this.selections.has(categoryId)) {
      this.selections.set(categoryId, new Map());
    }
    const categorySelections = this.selections.get(categoryId);

    const categoryEl = this.querySelector(`[data-category-id="${categoryId}"]`);
    const isRequired = categoryEl?.dataset.required === 'true';

    if (isRequired) {
      const totalQuantity = this.getTotalQuantityForCategory(categoryId);

      if (totalQuantity >= this.mainQuantity) {
        return;
      }
    }

    categorySelections.set(variantId, {
      variantId,
      quantity: 1,
      isRequired: isRequired,
      price: productData.price,
      variantImage: productData.variantImage || null,
      preOrder: productData.preOrder,
      dueDates: productData.dueDates,
      nextDay: productData.nextDay,
      productTitle: productData.title,
      variantTitle: productData.variantTitle,
      inventoryQuantity: productData.inventoryQuantity || 0,
      inventoryPolicy: productData.inventoryPolicy || 'deny',
      bespokeOptions: bespokeOptions || null
    });

    this.updateCategoryDisplay(categoryId);
    this.saveSelections();
    this.checkCompatibility();
    this.validateRequiredAddons();

    this.dispatchEvent(new CustomEvent('addon:selected', {
      detail: { categoryId, variantId },
      bubbles: true
    }));
  }

  /**
   * Builds HTML for a single variant
   * @param {Object} selection - Selection data
   * @param {String} variantId - Variant ID
   * @returns {String} HTML string
   */
  buildVariantHTML(selection, variantId) {
    const imageHtml = selection.variantImage
      ? `<img src="${selection.variantImage}" alt="${selection.productTitle}" class="product-addon-variant__image" loading="lazy">`
      : '';

    const variantInfo = selection.variantTitle !== 'Default Title'
      ? `<div class="product-addon-variant__title">
           <span class="product-addon-variant__checkmark">${icons.checkCircle()}</span>
           ${selection.variantTitle}
         </div>`
      : '';

    return `
      <div class="product-addon-variant" data-variant-id="${variantId}">
        ${imageHtml}
        <div class="product-addon-variant__info">
          <div class="product-addon-variant__name">${selection.productTitle}</div>
          ${variantInfo}
        </div>
        <div class="product-addon-variant__qty">
          <button type="button" class="product-addon-variant__qty-btn product-addon-variant__qty-minus" data-action="decrease" data-variant-id="${variantId}">
            ${icons.minus()}
          </button>
          <input type="number" class="product-addon-variant__qty-input" value="${selection.quantity}" min="0" readonly>
          <button type="button" class="product-addon-variant__qty-btn product-addon-variant__qty-plus" data-action="increase" data-variant-id="${variantId}">
            ${icons.plus()}
          </button>
        </div>
        <div class="product-addon-variant__price">+ ${this.formatPrice(selection.price * selection.quantity)}</div>
      </div>
    `;
  }

  /**
   * Builds HTML string for all selected variants
   * @param {Map} categorySelections - Map of variant selections
   * @returns {String} HTML string
   */
  buildVariantsHTML(categorySelections) {
    const variantsArray = Array.from(categorySelections.entries());
    return variantsArray.map(([variantId, selection]) =>
      this.buildVariantHTML(selection, variantId)
    ).join('');
  }

  /**
   * Updates plus button states based on category limit
   * @param {HTMLElement} category - Category element
   * @param {String} categoryId - Category ID
   */
  updatePlusButtonStates(category, categoryId) {
    const categoryEl = this.querySelector(`[data-category-id="${categoryId}"]`);
    const isRequired = categoryEl?.dataset.required === 'true';
    const plusButtons = category.querySelectorAll('[data-action="increase"]');

    if (isRequired) {
      const totalQuantity = this.getTotalQuantityForCategory(categoryId);
      const atLimit = totalQuantity >= this.mainQuantity;
      plusButtons.forEach(btn => {
        btn.disabled = atLimit;
      });
    } else {
      /* Optional: disable per-variant plus buttons at inventory limit */
      const categorySelections = this.selections.get(categoryId);
      plusButtons.forEach(btn => {
        const variantId = parseInt(btn.dataset.variantId, 10);
        const selection = categorySelections?.get(variantId);
        if (selection && selection.inventoryPolicy !== 'continue' && selection.inventoryQuantity > 0) {
          btn.disabled = selection.quantity >= selection.inventoryQuantity;
        }
      });
    }
  }

  /**
   * Renders the variant display HTML
   * @param {HTMLElement} category - Category element
   * @param {Map} categorySelections - Variant selections
   * @param {String} categoryId - Category ID
   */
  renderSelectedVariants(category, categorySelections, categoryId) {
    category.classList.add('product-addon-category--selected');

    let variantDisplay = category.querySelector('.product-addon-category__variants');
    if (!variantDisplay) {
      variantDisplay = document.createElement('div');
      variantDisplay.className = 'product-addon-category__variants';
      category.appendChild(variantDisplay);
    }

    const variantsHTML = this.buildVariantsHTML(categorySelections);
    variantDisplay.innerHTML = variantsHTML;

    this.updatePlusButtonStates(category, categoryId);
  }

  /**
   * Updates completion status indicators
   * @param {HTMLElement} category - Category element
   * @param {String} categoryId - Category ID
   */
  updateCategoryCompletionStatus(category, categoryId) {
    const totalQuantity = this.getTotalQuantityForCategory(categoryId);
    const requiredQuantity = this.mainQuantity;
    const isRequired = category.dataset.required === 'true';
    const isComplete = isRequired ? totalQuantity === requiredQuantity : totalQuantity > 0;

    const quantityCounter = category.querySelector('.product-addon-category__quantity-counter');
    const checkmark = category.querySelector('.product-addon-category__checkmark');

    /* Only show counter for required categories */
    if (isRequired && quantityCounter) {
      const currentSpan = quantityCounter.querySelector('.product-addon-category__quantity-current');
      const totalSpan = quantityCounter.querySelector('.product-addon-category__quantity-total');

      if (currentSpan) {
        currentSpan.textContent = totalQuantity;
      }
      if (totalSpan) {
        totalSpan.textContent = requiredQuantity;
      }
    }

    if (isRequired) {
      this.updateCompletionIndicators(quantityCounter, checkmark, isComplete);
    } else {
      /* Counter stays hidden for optional, but show checkmark when selections exist */
      quantityCounter?.setAttribute('hidden', '');
      if (checkmark) {
        if (isComplete) {
          checkmark.removeAttribute('hidden');
        } else {
          checkmark.setAttribute('hidden', '');
        }
      }
    }
  }

  /**
   * Updates category button text and classes
   * @param {HTMLElement} category - Category element
   * @param {String} text - Button text ('Change' or 'Select')
   */
  updateCategoryButton(category, text) {
    const button = category.querySelector('.product-addon-category__select, .product-addon-category__change');
    if (!button) return;

    if (text === 'Change') {
      button.textContent = 'Change';
      button.classList.remove('product-addon-category__select');
      button.classList.add('product-addon-category__change');
    } else {
      button.innerHTML = icons.arrowRight();
      button.classList.remove('product-addon-category__change');
      button.classList.add('product-addon-category__select');
    }
  }

  /**
   * Clears category display when no selections
   * @param {HTMLElement} category - Category element
   */
  clearCategoryDisplay(category) {
    category.classList.remove('product-addon-category--selected');

    const variantDisplay = category.querySelector('.product-addon-category__variants');
    if (variantDisplay) variantDisplay.remove();

    const quantityCounter = category.querySelector('.product-addon-category__quantity-counter');
    const checkmark = category.querySelector('.product-addon-category__checkmark');
    const isRequired = category.dataset.required === 'true';

    if (this.variantSelected) {
      /* Only show counter for required categories */
      if (isRequired && quantityCounter) {
        const currentSpan = quantityCounter.querySelector('.product-addon-category__quantity-current');
        if (currentSpan) currentSpan.textContent = '0';
        quantityCounter.removeAttribute('hidden');
      } else if (quantityCounter) {
        quantityCounter.setAttribute('hidden', '');
      }
      if (checkmark) {
        checkmark.setAttribute('hidden', '');
      }
    } else {
      if (quantityCounter) quantityCounter.setAttribute('hidden', '');
      if (checkmark) checkmark.setAttribute('hidden', '');
    }

    this.updateCategoryButton(category, 'Select');
  }

  /**
   * Updates the inline display for a category after selection
   * Shows multiple selected products with individual quantity controls
   * @param {String} categoryId - Category to update
   */
  updateCategoryDisplay(categoryId) {
    const category = this.querySelector(`[data-category-id="${categoryId}"]`);
    if (!category) return;

    const categorySelections = this.selections.get(categoryId);
    const hasSelections = categorySelections && categorySelections.size > 0;

    if (hasSelections) {
      this.renderSelectedVariants(category, categorySelections, categoryId);
      requestAnimationFrame(() => {
        this.updateCategoryCompletionStatus(category, categoryId);
      });
      this.updateCategoryButton(category, 'Change');
    } else {
      this.clearCategoryDisplay(category);
    }
  }

  /**
   * Sets up event handlers for variant quantity controls
   * Attached once to the component and handles all quantity button clicks
   */
  setupQuantityControls() {
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-action]');
      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const variantId = parseInt(button.dataset.variantId, 10);
      if (!variantId) {
        return;
      }

      const category = button.closest('[data-category-id]');
      if (!category) {
        return;
      }

      const categoryId = category.dataset.categoryId;
      const categorySelections = this.selections.get(categoryId);
      if (!categorySelections) {
        return;
      }

      const selection = categorySelections.get(variantId);
      if (!selection) {
        return;
      }

      const action = button.dataset.action;

      if (action === 'decrease') {
        selection.quantity--;
        if (selection.quantity === 0) {
          categorySelections.delete(variantId);

          if (categorySelections.size === 0) {
            this.selections.delete(categoryId);
          }

          this.dispatchEvent(new CustomEvent('addon:deselected', {
            detail: { categoryId, variantId },
            bubbles: true
          }));
        } else {
          /**
           * Dispatch event to update cart total when quantity decreases but not to 0
           */
          this.dispatchEvent(new CustomEvent('addon:selected', {
            detail: { categoryId, variantId },
            bubbles: true
          }));
        }
      } else if (action === 'increase') {
        const isRequired = category.dataset.required === 'true';

        if (isRequired) {
          const totalQuantity = this.getTotalQuantityForCategory(categoryId);
          if (totalQuantity >= this.mainQuantity) return;
        } else {
          /* Optional: respect per-variant inventory unless oversell-allowed */
          if (selection.inventoryPolicy !== 'continue' && selection.inventoryQuantity > 0) {
            if (selection.quantity >= selection.inventoryQuantity) return;
          }
        }

        selection.quantity++;

        this.dispatchEvent(new CustomEvent('addon:selected', {
          detail: { categoryId, variantId },
          bubbles: true
        }));
      }

      this.updateCategoryDisplay(categoryId);
      this.saveSelections();
      this.validateRequiredAddons();
    });
  }

  /**
   * Formats price in cents to currency string
   * @param {Number} cents - Price in cents
   * @returns {String} Formatted price
   */
  formatPrice(cents) {
    const currency = window.Shopify?.currency?.active || 'GBP';
    const amount = cents / 100;

    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Sets up compatibility checking when selections change
   */
  setupCompatibilityListeners() {
    this.addEventListener('addon:selected', () => {
      this.checkCompatibility();
    });
    this.addEventListener('addon:deselected', () => {
      this.checkCompatibility();
    });
  }

  /**
   * Checks compatibility and hides/shows categories accordingly
   */
  checkCompatibility() {
    const selectedCategories = Array.from(this.selections.keys());

    const incompatibleWith = selectedCategories
      .map(id => this.compatibilityRules[id])
      .find(rule => rule !== undefined);

    if (incompatibleWith) {
      this.applyCompatibilityRules(incompatibleWith);
    } else {
      this.clearCompatibilityRules();
    }
  }

  /**
   * Hides incompatible categories and shows warning
   * @param {Object} rule - Compatibility rule object
   */
  applyCompatibilityRules(rule) {
    const categories = this.querySelectorAll('.product-addon-category');

    categories.forEach(category => {
      const categoryId = category.dataset.categoryId;

      if (rule.hides.includes(categoryId)) {
        category.classList.add('is-hidden');

        if (this.selections.has(categoryId)) {
          this.clearSelection(categoryId);
        }
      }
    });

    this.showCompatibilityWarning(rule.displayName);
  }

  /**
   * Shows all categories and hides warning
   */
  clearCompatibilityRules() {
    const categories = this.querySelectorAll('.product-addon-category');
    categories.forEach(category => {
      category.classList.remove('is-hidden');
    });

    this.hideCompatibilityWarning();
  }

  /**
   * Displays compatibility warning message
   * @param {String} categoryName - Name of incompatible category
   */
  showCompatibilityWarning(categoryName) {
    const warning = this.querySelector('.product-addons__compatibility-warning');
    const text = this.querySelector('.product-addons__compatibility-text');

    if (!warning || !text) return;

    const messageTemplate = 'Some options have been hidden as they are incompatible with %category%.';
    const message = messageTemplate.replace('%category%', categoryName);

    text.textContent = message;
    warning.hidden = false;
  }

  /**
   * Hides compatibility warning message
   */
  hideCompatibilityWarning() {
    const warning = this.querySelector('.product-addons__compatibility-warning');
    if (warning) {
      warning.hidden = true;
    }
  }

  /**
   * Resets all required category selections when main product quantity changes
   * Clears selections, resets counters, and dispatches deselected events
   */
  resetRequiredSelections() {
    const requiredCategories = this.querySelectorAll('[data-required="true"]');
    requiredCategories.forEach(category => {
      const categoryId = category.dataset.categoryId;
      if (this.selections.has(categoryId)) {
        this.selections.delete(categoryId);
        this.clearCategoryDisplay(category);

        this.dispatchEvent(new CustomEvent('addon:deselected', {
          detail: { categoryId },
          bubbles: true
        }));
      }
    });

    this.saveSelections();

    /* Update total spans to reflect new quantity */
    requiredCategories.forEach(category => {
      const totalSpan = category.querySelector('.product-addon-category__quantity-total');
      if (totalSpan) totalSpan.textContent = this.mainQuantity;
    });
  }

  /**
   * Clears a selection for a specific category
   * @param {String} categoryId - Category to clear
   */
  clearSelection(categoryId) {
    this.selections.delete(categoryId);
    this.updateCategoryDisplay(categoryId);
    this.saveSelections();
    this.validateRequiredAddons();

    this.dispatchEvent(new CustomEvent('addon:deselected', {
      detail: { categoryId },
      bubbles: true
    }));
  }

  /**
   * Validates all required add-ons have correct quantity sums
   * @returns {Boolean} True if all valid
   */
  validateRequiredAddons() {
    /* Optional add-ons should never control ATC button state */
    if (!this.isRequiredComponent) return true;

    if (!this.variantSelected) {
      this.toggleAddToCart(false);
      return false;
    }

    const requiredCategories = this.querySelectorAll('[data-required="true"]:not(.is-hidden):not(.is-unavailable):not(.is-pending)');
    const allValid = Array.from(requiredCategories).every(category => {
      const categoryId = category.dataset.categoryId;
      const categorySelections = this.selections.get(categoryId);

      if (!categorySelections || categorySelections.size === 0) {
        return false;
      }

      let totalQuantity = 0;
      categorySelections.forEach(selection => {
        totalQuantity += selection.quantity;
      });

      return totalQuantity === this.mainQuantity;
    });

    this.toggleAddToCart(allValid);
    return allValid;
  }

  /**
   * Toggles add-to-cart button state
   * @param {Boolean} isValid - Whether validation passed
   */
  toggleAddToCart(isValid) {
    const atcButton = document.querySelector('[data-add-to-cart]');
    if (!atcButton) return;

    this.dataset.addonsValid = isValid;

    if (!isValid && this.variantSelected) {
      atcButton.disabled = true;
      atcButton.classList.add('disabled');
    } else if (isValid && this.variantSelected) {
      atcButton.disabled = false;
      atcButton.classList.remove('disabled');
    }
  }

  /**
   * Adds selected add-ons to cart after main products are added
   * Uses batch add API to add all items in a single request
   * @param {String} groupId - GroupId from main product (optional, generates if not provided)
   */
  async addAddonsToCart(groupId) {
    if (this.selections.size === 0) return;

    if (!groupId) {
      groupId = new Date().getTime().toString();
    }

    const addons = this.buildAddonItems(groupId);
    if (addons.length === 0) return;

    try {
      await this.addItemsToCart(addons);
    } catch (error) {
      /* Error adding add-ons to cart */
    }

    const minicart = document.querySelector('mini-cart');
    if (minicart) {
      minicart.updateSectionContents();
    }
  }

  /**
   * Builds add-on items array from all selections
   * @param {String} groupId - Shared GroupId for bundling
   * @returns {Array} Array of add-on items to add to cart
   */
  buildAddonItems(groupId) {
    const items = [];

    this.selections.forEach((categorySelections, categoryId) => {
      categorySelections.forEach((selection, variantId) => {
        const addonProperties = {
          'GroupId': groupId,
          'groupedProduct': true,
          'required': selection.isRequired || false,
          'min': this.mainQuantity,
          'aog': categoryId
        };

        if (selection.preOrder) {
          addonProperties.preOrder = selection.preOrder;
        }
        if (selection.dueDates) {
          addonProperties.dueDates = selection.dueDates;
        }
        if (selection.nextDay === 'true' || selection.nextDay === true) {
          addonProperties.next_day = 'true';
        }

        /* Add bespoke option values as cart properties */
        if (selection.bespokeOptions) {
          const bespoke = selection.bespokeOptions;
          const isStructuredFormat = bespoke.dropdowns || bespoke.dimensions || bespoke.confirmation !== undefined;

          if (isStructuredFormat) {
            /* Structured format from fresh selections */
            let totalPriceAdjustment = 0;

            if (bespoke.dropdowns) {
              Object.entries(bespoke.dropdowns).forEach(([dropdownId, data]) => {
                if (data.label && data.value) {
                  addonProperties[data.label] = data.value;
                  if (data.swatch) {
                    addonProperties[`_${data.label}_swatch`] = data.swatch;
                  }
                  if (data.priceAdjustment) {
                    totalPriceAdjustment += parseInt(data.priceAdjustment, 10) || 0;
                  }
                }
              });
            }

            /* Pre-calculated total includes both dropdown and dimension adjustments */
            if (bespoke.totalPriceAdjustment !== undefined) {
              totalPriceAdjustment = bespoke.totalPriceAdjustment;
            }

            if (totalPriceAdjustment > 0) {
              addonProperties._priceAdjustment = totalPriceAdjustment.toString();
            }

            if (bespoke.dimensions && bespoke.dimensions.length > 0) {
              bespoke.dimensions.forEach(dim => {
                if (dim.dim1) addonProperties[`${dim.label} ${dim.label1} (mm)`] = dim.dim1;
                if (dim.dim2) addonProperties[`${dim.label} ${dim.label2} (mm)`] = dim.dim2;
              });
              addonProperties._unitPreference = isImperial() ? 'inches' : 'mm';
            }

            if (bespoke.confirmation && bespoke.confirmationLabel) {
              addonProperties[bespoke.confirmationLabel] = 'Yes';
            }
          } else {
            /* Flat format from bundle edit - copy properties directly */
            Object.entries(bespoke).forEach(([key, value]) => {
              if (value !== null && value !== undefined && value !== '') {
                addonProperties[key] = value;
              }
            });
          }

          addonProperties._hasOptions = 'true';
        }

        items.push({
          id: selection.variantId,
          quantity: selection.quantity,
          properties: addonProperties
        });
      });
    });

    return items;
  }

  /**
   * Adds multiple items to cart in a single batch request
   * Prevents 409 Conflict errors from concurrent cart operations
   * @param {Array} items - Array of items with id, quantity, and properties
   * @returns {Promise}
   */
  addItemsToCart(items) {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    };

    return fetch(window.routes.cart_add_url, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          throw new Error(response.description || 'Error adding to cart');
        }
        return response;
      });
  }

  /**
   * Adds a single item to the cart via fetch
   * @param {Object} item - Item with id, quantity, and properties
   * @returns {Promise}
   */
  addItemToCart(item) {
    const formData = new FormData();
    formData.append('id', item.id);
    formData.append('quantity', item.quantity);

    if (item.properties && Object.keys(item.properties).length > 0) {
      for (const key in item.properties) {
        if (item.properties.hasOwnProperty(key)) {
          formData.append('properties[' + key + ']', item.properties[key]);
        }
      }
    }

    const config = {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    return fetch(window.routes.cart_add_url, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          throw new Error(response.description || 'Error adding to cart');
        }
        return response;
      });
  }

  /**
   * Gets main product properties from bespoke options form
   * @returns {Object} Properties object with custom options (Width, Height, etc.)
   */
  getMainProductProperties() {
    const properties = {};
    const bespokeForm = document.querySelector('[data-bespoke-options]');

    if (!bespokeForm) {
      return properties;
    }

    const inputs = bespokeForm.querySelectorAll('[name^="properties["]');
    inputs.forEach(input => {
      if (input.value) {
        const match = input.name.match(/properties\[([^\]]+)\]/);
        if (match) {
          properties[match[1]] = input.value;
        }
      }
    });

    if (Object.keys(properties).length > 0 && window.noAddons && window.hasOptions) {
      properties.hasOptions = true;
    }

    return properties;
  }

  /**
   * Converts Map to nested object structure
   * @returns {Object} Selections as nested object
   */
  convertSelectionsToObject() {
    return Object.fromEntries(
      Array.from(this.selections.entries()).map(([categoryId, categorySelections]) => [
        categoryId,
        Object.fromEntries(categorySelections)
      ])
    );
  }

  /**
   * Saves current selections to sessionStorage
   */
  saveSelections() {
    const productId = this.dataset.productId;
    const drawerId = this.dataset.drawerId || 'default';
    const selectionsObj = this.convertSelectionsToObject();
    sessionStorage.setItem(`addon-selections-${productId}-${drawerId}`, JSON.stringify(selectionsObj));
  }

  /**
   * Loads saved selections from sessionStorage
   * Validates quantities match current main product quantity
   */
  loadSavedSelections() {
    const productId = this.dataset.productId;
    const drawerId = this.dataset.drawerId || 'default';
    const saved = sessionStorage.getItem(`addon-selections-${productId}-${drawerId}`);

    if (saved) {
      try {
        const selectionsObj = JSON.parse(saved);

        Object.entries(selectionsObj).forEach(([categoryId, categoryData]) => {
          const categorySelections = new Map();

          Object.entries(categoryData).forEach(([variantId, selection]) => {
            categorySelections.set(variantId, selection);
          });

          if (categorySelections.size > 0) {
            this.selections.set(categoryId, categorySelections);
            this.updateCategoryDisplay(categoryId);
          }
        });

        this.validateRequiredAddons();
      } catch (e) {
        /* Failed to load saved add-on selections */
      }
    }
  }

  /**
   * Loads bundle data from sessionStorage (for cart edit/duplicate)
   * Restores full PDP state: variant quantities, addon selections, ATC button
   */
  loadEditBundle() {
    const editData = sessionStorage.getItem('editBundle');
    if (!editData) return;

    try {
      const bundle = JSON.parse(editData);

      const productHandle = this.dataset.productHandle;
      if (bundle.productHandle !== productHandle) return;

      /**
       * Only consume categories that exist in this component.
       * Both required and optional <product-addons> call this method,
       * so each takes only the categories it owns and leaves the rest
       * in sessionStorage for the other component.
       */
      const ownedCategories = [];
      const remainingSelections = {};

      Object.entries(bundle.selections).forEach(([categoryId, categoryData]) => {
        const categoryEl = this.querySelector(`[data-category-id="${categoryId}"]`);

        if (categoryEl) {
          ownedCategories.push(categoryId);
          const categorySelections = new Map();

          Object.entries(categoryData).forEach(([variantId, selection]) => {
            categorySelections.set(parseInt(variantId, 10), {
              variantId: selection.variantId,
              quantity: selection.quantity,
              isRequired: selection.isRequired || categoryEl?.dataset.required === 'true',
              price: selection.price,
              productTitle: selection.productTitle,
              variantTitle: selection.variantTitle,
              variantImage: selection.variantImage || null,
              preOrder: selection.preOrder,
              dueDates: selection.dueDates,
              nextDay: selection.nextDay,
              bespokeOptions: selection.bespokeOptions || null
            });
          });

          if (categorySelections.size > 0) {
            this.selections.set(categoryId, categorySelections);
          }
        } else {
          remainingSelections[categoryId] = categoryData;
        }
      });

      if (ownedCategories.length === 0) {
        /**
         * If no categories can be matched and old bundle was already removed,
         * clear stale sessionStorage to prevent infinite retry on every page load.
         */
        if (bundle._oldBundleRemoved) {
          sessionStorage.removeItem('editBundle');
        }
        return;
      }

      /**
       * Restore main product variant quantities on the PDP variant selector.
       * Only the first component to run does this (sets _variantsRestored flag).
       */
      const tlpVariants = bundle.tlpVariants || [];
      const totalTlpQuantity = tlpVariants.reduce((sum, v) => sum + v.quantity, 0);

      if (totalTlpQuantity > 0 && !bundle._variantsRestored) {
        tlpVariants.forEach(tlpVariant => {
          const input = document.querySelector(
            `.product-variants__qty-input[data-variant-id="${tlpVariant.variantId}"]`
          );
          if (input) {
            input.value = tlpVariant.quantity;
            const container = input.closest('.product-variants__quantity');
            if (container) {
              const minusBtn = container.querySelector('[data-action="decrease"]');
              const plusBtn = container.querySelector('[data-action="increase"]');
              const min = parseInt(input.min) || 0;
              const max = input.max ? parseInt(input.max) : null;
              if (minusBtn) minusBtn.disabled = tlpVariant.quantity <= min;
              if (plusBtn) plusBtn.disabled = max !== null && tlpVariant.quantity >= max;
            }
          }
        });
        bundle._variantsRestored = true;
      }

      /**
       * Restore bespoke option values on the main product form.
       * Only the first component to run does this (same guard as variants).
       */
      if (bundle.tlpBespokeProperties && !bundle._bespokeRestored) {
        const bespokeComponent = document.querySelector('product-bespoke-options');
        if (bespokeComponent?.restoreFromProperties) {
          bespokeComponent.restoreFromProperties(bundle.tlpBespokeProperties);
        }
        bundle._bespokeRestored = true;
      }

      /**
       * Enable addons and set main quantity so selections render correctly
       * and validation can pass.
       */
      if (totalTlpQuantity > 0) {
        this.mainQuantity = totalTlpQuantity;
      }
      this.enableAddons();

      /**
       * Update product-variants price BEFORE rendering selections.
       * This dispatches variant:change which triggers handleVariantChange → enableAddons
       * (resets counters). By doing this first, the subsequent updateCategoryDisplay
       * calls set the final correct state without being overwritten.
       */
      const productVariants = document.querySelector('product-variants');
      if (productVariants?.updateTotalPrice) {
        productVariants.updateTotalPrice();
      }

      /**
       * Render selections LAST so counters and variant HTML aren't overwritten
       * by enableAddons resets triggered by variant:change above.
       * Force synchronous counter update to avoid requestAnimationFrame timing issues.
       */
      ownedCategories.forEach((categoryId) => {
        this.updateCategoryDisplay(categoryId);
        const category = this.querySelector(`[data-category-id="${categoryId}"]`);
        if (category) {
          this.updateCategoryCompletionStatus(category, categoryId);
        }
      });

      if (bundle.type === 'edit' && !bundle._oldBundleRemoved) {
        this.removeOldBundle(bundle.groupId);
        bundle._oldBundleRemoved = true;
      }

      if (Object.keys(remainingSelections).length > 0) {
        bundle.selections = remainingSelections;
        sessionStorage.setItem('editBundle', JSON.stringify(bundle));
      } else {
        sessionStorage.removeItem('editBundle');
      }

      this.saveSelections();
      this.validateRequiredAddons();
    } catch (e) {
      sessionStorage.removeItem('editBundle');
    }
  }

  /**
   * Removes old bundle from cart
   * @param {String} groupId - The GroupId to remove
   */
  async removeOldBundle(groupId) {
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await cartResponse.json();

      const itemsToRemove = cart.items.filter(item => {
        return item.properties && item.properties.GroupId === groupId;
      });

      if (itemsToRemove.length === 0) return;

      const updates = {};
      itemsToRemove.forEach(item => {
        updates[item.key] = 0;
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      /**
       * Refresh mini-cart to update the header cart count badge
       */
      const minicart = document.querySelector('mini-cart');
      if (minicart?.updateSectionContents) {
        minicart.updateSectionContents();
      }
    } catch (error) {
      /* Failed to remove old bundle */
    }
  }

  /**
   * Updates display for all categories (called when main quantity changes)
   */
  updateAllCategoryDisplays() {
    this.selections.forEach((categorySelections, categoryId) => {
      this.updateCategoryDisplay(categoryId);
    });
  }
}

/**
 * Product Add-ons Drawer Component
 * Manages the drawer UI for selecting add-on products
 */
class ProductAddonsDrawer extends DrawerModal {
  constructor() {
    super();
    this.currentCategoryId = null;
    this.categoriesData = null;
    this.legacyData = null;
    this.parentComponent = null;

    /** @type {Set<HTMLInputElement>} Dimension inputs that have been blurred */
    this.touchedDimensions = new Set();
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.loadCategoriesData();
    this.setupCloseHandlers();
    this.setupUnitChangeListener();
    this.prefetchLegacyCategories();
  }

  /**
   * Sets up listener for unit toggle changes to update dimension fields in drawer
   */
  setupUnitChangeListener() {
    document.addEventListener('unit:changed', (event) => {
      this.updateDimensionUnits(event.detail.isImperial);
      this.updateDropdownUnits();
    });
  }

  /**
   * Updates all dropdown option labels based on current unit preference
   */
  updateDropdownUnits() {
    const drawerContent = this.querySelector('.addon-drawer__content');
    if (drawerContent) {
      updateDropdownUnits(drawerContent, (cents) => this.formatMoney(cents));
    }
  }

  /**
   * Formats money value from cents to display string
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    const pounds = (cents / 100).toFixed(2);
    return `£${pounds}`;
  }

  /**
   * Updates all dimension field units and range hints based on selected unit
   * @param {boolean} toImperial - Whether to show imperial (inches) or metric (mm)
   */
  updateDimensionUnits(toImperial) {
    const unitLabels = this.querySelectorAll('.product-bespoke-dimension__unit');
    const rangeHints = this.querySelectorAll('.product-bespoke-dimension__range-hint');
    const dimInputs = this.querySelectorAll('.product-bespoke-dimension__input');

    unitLabels.forEach(label => {
      label.textContent = toImperial ? '"' : 'mm';
    });

    rangeHints.forEach(hint => {
      const data = parseDimensionData(hint);
      if (!data.valid) return;
      hint.textContent = formatDimensionRange(data.minMm, data.maxMm, toImperial);
    });

    dimInputs.forEach(input => {
      const data = parseDimensionData(input);
      if (!data.valid) return;
      updateInputMinMax(input, data.minMm, data.maxMm, toImperial);
      convertInputValue(input, toImperial);
    });
  }

  /**
   * Formats a dimension value in the current unit
   * @param {number} mmValue - Value in millimeters
   * @returns {string} Formatted value with unit symbol
   */
  formatDimensionValue(mmValue) {
    return formatDimension(mmValue);
  }

  /**
   * Loads category data from embedded JSON (metaobject and legacy)
   */
  loadCategoriesData() {
    const dataScript = this.querySelector('[data-addon-categories]');
    if (dataScript) {
      try {
        this.categoriesData = JSON.parse(dataScript.textContent);
      } catch (e) {
        /* Failed to parse add-ons data */
      }
    }

    /* LEGACY FALLBACK - Remove after data migration complete */
    const legacyScript = this.querySelector('[data-legacy-addon-categories]');
    if (legacyScript) {
      try {
        this.legacyData = JSON.parse(legacyScript.textContent);
        this.decodeLegacyLabels();
      } catch (e) {
        /* Failed to parse legacy add-ons data */
      }
    }
  }

  /**
   * Decodes HTML entities in legacy category labels
   * Liquid HTML-escapes strings (e.g. & → &amp;) even inside script tags,
   * so labels need decoding before use in textContent
   */
  decodeLegacyLabels() {
    if (!this.legacyData?.legacyCategories) return;
    const textarea = document.createElement('textarea');
    this.legacyData.legacyCategories.forEach(category => {
      if (category.label) {
        textarea.innerHTML = category.label;
        category.label = textarea.value;
      }
    });
  }

  /**
   * Finds the parent ProductAddons component that owns this drawer
   * @returns {ProductAddons|null} The parent component
   */
  findParentComponent() {
    if (this.parentComponent) return this.parentComponent;

    const drawerId = this.dataset.drawerId || 'default';
    const parent = document.querySelector(`product-addons[data-drawer-id="${drawerId}"]`);
    if (parent) {
      this.parentComponent = parent;
    }
    return parent;
  }

  /**
   * LEGACY FALLBACK - Remove after data migration complete
   * Pre-fetches all legacy categories via Storefront API on page load
   * Hides category cards that return zero available products
   */
  async prefetchLegacyCategories() {
    if (!this.legacyData?.legacyCategories?.length) return;

    const parent = this.findParentComponent();

    try {
      const { fetchProductsBySku } = await import('./storefront-api.js');

      const fetchPromises = this.legacyData.legacyCategories.map(async (category) => {
        if (category.products.length > 0) return;

        const products = await fetchProductsBySku(category.id, category.skus);
        category.products = products;

        if (!parent) return;

        const categoryEl = parent.querySelector(`[data-category-id="${category.id}"]`);
        if (!categoryEl) return;

        categoryEl.classList.remove('is-pending');
        if (products.length === 0) {
          categoryEl.classList.add('is-unavailable');
        }
      });

      await Promise.all(fetchPromises);
    } catch (error) {
      /* Failed to prefetch legacy categories */
    }

    /* After all fetches complete, check if the entire section should be hidden */
    if (parent) {
      const allCategories = parent.querySelectorAll('.product-addon-category');
      const visibleCategories = Array.from(allCategories).filter(
        el => !el.classList.contains('is-hidden') && !el.classList.contains('is-unavailable')
      );

      if (visibleCategories.length === 0) {
        parent.hidden = true;
      }

      if (parent.validateRequiredAddons) {
        parent.validateRequiredAddons();
      }
    }
  }

  /**
   * Checks if a category is required by looking at the parent component
   * @param {String} categoryId - Category ID to check
   * @returns {Boolean} Whether the category is required
   */
  isCategoryRequired(categoryId) {
    if (!this.parentComponent) return true;
    const categoryEl = this.parentComponent.querySelector(`[data-category-id="${categoryId}"]`);
    return categoryEl?.dataset.required === 'true';
  }

  /**
   * Opens drawer for a specific category
   * @param {String} categoryId - The category to display
   * @param {ProductAddons} parentComponent - The parent add-ons component
   */
  open(categoryId, parentComponent) {
    if (parentComponent) {
      this.parentComponent = parentComponent;
    }

    const isSameCategory = this.loadedCategoryId === categoryId;
    this.currentCategoryId = categoryId;

    if (isSameCategory && !this.hasSelectionDrift(categoryId)) {
      this.refreshDrawerCounters(categoryId);
    } else {
      this.loadCategoryContent(categoryId);
    }

    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      super.open();

      /**
       * Scroll to top when drawer opens
       */
      const drawerContent = this.querySelector('.drawer-content');
      if (drawerContent) {
        drawerContent.scrollTop = 0;
      }
    });
  }

  /**
   * Closes the drawer
   */
  close() {
    this.removeAttribute('open');
    this.touchedDimensions.clear();
    super.close();
  }

  /**
   * Sets up close button handlers
   */
  setupCloseHandlers() {
    const closeButton = this.querySelector('.drawer-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.close();
      }

      if (!event.target.closest('.product-bespoke-options__tooltip-wrap')) {
        this.closeAllBespokeTooltips();
      }
    });
  }

  /**
   * Parses Shopify rich text JSON to HTML
   * @param {Object|String} richText - Rich text JSON object or string
   * @returns {String} HTML string
   */
  parseRichText(richText) {
    if (!richText) return '';

    let data = richText;
    if (typeof richText === 'string') {
      try {
        data = JSON.parse(richText);
      } catch (e) {
        return richText;
      }
    }

    if (!data.children || !Array.isArray(data.children)) {
      return '';
    }

    const parseNode = (node) => {
      if (node.type === 'text') {
        return node.value || '';
      }

      if (node.type === 'link') {
        const href = node.url || '#';
        const text = node.children ? node.children.map(parseNode).join('') : '';
        return `<a href="${href}">${text}</a>`;
      }

      if (node.type === 'paragraph') {
        const content = node.children ? node.children.map(parseNode).join('') : '';
        return `<p>${content}</p>`;
      }

      if (node.children) {
        return node.children.map(parseNode).join('');
      }

      return '';
    };

    return data.children.map(parseNode).join('');
  }

  /**
   * Loads category-specific content into drawer
   * Checks metaobject data first, then falls back to legacy GraphQL fetch
   * @param {String} categoryId - Category ID to load
   */
  async loadCategoryContent(categoryId) {
    /* Try metaobject data first */
    const category = this.categoriesData?.categories?.find(c => c.id === categoryId);

    if (category) {
      this.renderCategoryContent(category);
      return;
    }

    /* LEGACY FALLBACK - fetch via Storefront API */
    const legacyCategory = this.legacyData?.legacyCategories?.find(c => c.id === categoryId);
    if (!legacyCategory) return;

    const loadingEl = this.querySelector('.addon-drawer__loading');
    const contentEl = this.querySelector('.addon-drawer__content');

    /* Check cache — products array is populated after first fetch */
    if (legacyCategory.products.length > 0) {
      this.renderCategoryContent(legacyCategory);
      return;
    }

    /* Show loading state while fetching */
    if (loadingEl) loadingEl.hidden = false;
    if (contentEl) contentEl.hidden = true;

    try {
      const { fetchProductsBySku } = await import('./storefront-api.js');
      const products = await fetchProductsBySku(categoryId, legacyCategory.skus);
      legacyCategory.products = products;

      if (products.length === 0) {
        this.hideLegacyCategory(categoryId);
        this.close();
        return;
      }

      this.renderCategoryContent(legacyCategory);
    } catch (error) {
      if (loadingEl) {
        loadingEl.innerHTML = '<p>Failed to load products. Please try again.</p>';
      }
    }
  }

  /**
   * LEGACY FALLBACK - Remove after data migration complete
   * Hides a legacy category card when no products are available
   * @param {String} categoryId - Category ID to hide
   */
  hideLegacyCategory(categoryId) {
    const parent = this.findParentComponent();
    if (!parent) return;

    const categoryEl = parent.querySelector(`[data-category-id="${categoryId}"]`);
    if (categoryEl) {
      categoryEl.classList.remove('is-pending');
      categoryEl.classList.add('is-unavailable');
    }
  }

  /**
   * Renders category content into the drawer
   * @param {Object} category - Category data object with label, note, products
   */
  renderCategoryContent(category) {
    const loadingEl = this.querySelector('.addon-drawer__loading');
    const contentEl = this.querySelector('.addon-drawer__content');
    const titleEl = this.querySelector('.addon-drawer__title');
    const descriptionEl = this.querySelector('.addon-drawer__description');
    const noteContent = this.querySelector('.addon-drawer__category-note');

    if (!loadingEl || !contentEl || !titleEl || !descriptionEl || !noteContent) return;

    loadingEl.hidden = true;
    contentEl.hidden = false;

    titleEl.textContent = category.label;
    descriptionEl.hidden = true;

    const categoryId = category.id;

    /**
     * Initialize drawer counter and checkmark
     * Counter only shows for required categories
     */
    const drawerCounter = this.querySelector('.addon-drawer__quantity-counter');
    const drawerCheckmark = this.querySelector('.addon-drawer__checkmark');
    const isRequired = this.isCategoryRequired(categoryId);

    if (drawerCounter && this.parentComponent) {
      if (isRequired) {
        const currentSpan = drawerCounter.querySelector('.addon-drawer__quantity-current');
        const totalSpan = drawerCounter.querySelector('.addon-drawer__quantity-total');

        const totalQuantity = this.parentComponent.getTotalQuantityForCategory(categoryId);
        const requiredQuantity = this.parentComponent.mainQuantity;
        const isComplete = totalQuantity === requiredQuantity;

        if (currentSpan) currentSpan.textContent = totalQuantity;
        if (totalSpan) totalSpan.textContent = requiredQuantity;

        this.parentComponent.updateCompletionIndicators(drawerCounter, drawerCheckmark, isComplete);
      } else {
        /* Hide counter and checkmark for optional categories */
        drawerCounter.setAttribute('hidden', '');
        if (drawerCheckmark) drawerCheckmark.setAttribute('hidden', '');
      }
    }

    let noteHtml = '';
    if (category.note) {
      noteHtml = this.parseRichText(category.note);
    }
    const hasNote = noteHtml !== '';

    if (hasNote) {
      noteContent.innerHTML = noteHtml;
      noteContent.hidden = false;
    } else {
      noteContent.hidden = true;
    }

    this.renderProducts(category.products, categoryId);
    this.loadedCategoryId = categoryId;
  }

  /**
   * Refreshes drawer counters without re-rendering products
   * Used when reopening drawer for the same category to preserve form state
   * @param {String} categoryId - Category ID to refresh counters for
   */
  refreshDrawerCounters(categoryId) {
    const drawerCounter = this.querySelector('.addon-drawer__quantity-counter');
    const drawerCheckmark = this.querySelector('.addon-drawer__checkmark');
    const isRequired = this.isCategoryRequired(categoryId);

    if (drawerCounter && this.parentComponent) {
      if (isRequired) {
        const currentSpan = drawerCounter.querySelector('.addon-drawer__quantity-current');
        const totalSpan = drawerCounter.querySelector('.addon-drawer__quantity-total');

        const totalQuantity = this.parentComponent.getTotalQuantityForCategory(categoryId);
        const requiredQuantity = this.parentComponent.mainQuantity;
        const isComplete = totalQuantity === requiredQuantity;

        if (currentSpan) currentSpan.textContent = totalQuantity;
        if (totalSpan) totalSpan.textContent = requiredQuantity;

        this.parentComponent.updateCompletionIndicators(drawerCounter, drawerCheckmark, isComplete);
      }
    }
  }

  /**
   * Checks whether the drawer DOM selection state has drifted from the
   * parent component's actual selections (e.g. quantity reduced to 0 on the card)
   * @param {String} categoryId - Category ID to check
   * @returns {Boolean} True if DOM and selections are out of sync
   */
  hasSelectionDrift(categoryId) {
    if (!this.parentComponent) return false;

    const categorySelections = this.parentComponent.selections.get(categoryId);
    const selectedRows = this.querySelectorAll('.addon-variant-row.is-selected');

    if (!categorySelections || categorySelections.size === 0) {
      return selectedRows.length > 0;
    }

    if (selectedRows.length !== categorySelections.size) return true;

    for (const row of selectedRows) {
      const variantId = parseInt(row.dataset.variantId, 10);
      if (!categorySelections.has(variantId)) return true;
    }

    return false;
  }

  /**
   * Updates drawer counter and checkmark based on current selections
   */
  updateDrawerCounter() {
    const drawerCounter = this.querySelector('.addon-drawer__quantity-counter');
    const drawerCheckmark = this.querySelector('.addon-drawer__checkmark');

    if (!this.parentComponent || !this.currentCategoryId) return;

    const totalQuantity = this.parentComponent.getTotalQuantityForCategory(this.currentCategoryId);
    const isRequired = this.isCategoryRequired(this.currentCategoryId);

    if (isRequired && drawerCounter) {
      const requiredQuantity = this.parentComponent.mainQuantity;
      const isComplete = totalQuantity === requiredQuantity;

      const currentSpan = drawerCounter.querySelector('.addon-drawer__quantity-current');
      if (currentSpan) currentSpan.textContent = totalQuantity;

      this.parentComponent.updateCompletionIndicators(drawerCounter, drawerCheckmark, isComplete);
    } else {
      /* Hide counter and checkmark for optional categories */
      if (drawerCounter) drawerCounter.setAttribute('hidden', '');
      if (drawerCheckmark) drawerCheckmark.setAttribute('hidden', '');
    }
  }

  /**
   * Renders product list in drawer with accordion structure
   * Each product can have multiple variants and bespoke options
   * @param {Array} products - Array of product variant data
   * @param {String} categoryId - Category ID
   */
  renderProducts(products, categoryId) {
    const productsContainer = this.querySelector('.addon-drawer__products');
    if (!productsContainer) return;

    productsContainer.innerHTML = '';

    // sort product add-ons in ascending order
    const productGroups = this.groupVariantsByProduct(
      products.toSorted((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0))
    );

    productGroups.forEach((variants, productId) => {
      if (!variants.length) return;
      const firstVariant = variants[0];
      const hasBespokeOptions = this.hasBespokeOptions(firstVariant.bespokeOptions);

      const productAccordion = document.createElement('div');
      productAccordion.className = 'addon-product-accordion';
      productAccordion.dataset.productId = productId;

      /**
       * Check if any variant of this product is selected
       * If yes, open the accordion automatically
       */
      const categorySelections = this.parentComponent?.selections?.get(categoryId);
      const hasSelection = variants.some(variant =>
        categorySelections?.has(variant.id)
      );
      if (hasSelection) {
        productAccordion.classList.add('is-open');
      }

      const header = document.createElement('button');
      header.type = 'button';
      header.className = 'addon-product-accordion__header';
      const imageHtml = firstVariant.productImage
        ? `<img class="addon-product-accordion__image" src="${firstVariant.productImage}" alt="${firstVariant.title}" width="100" height="100" loading="lazy">`
        : '';

      header.innerHTML = `
        ${imageHtml}
        <div class="addon-product-accordion__info">
          <span class="addon-product-accordion__title">${firstVariant.title}</span>
        </div>
        <span class="addon-product-accordion__icon">${icons.arrowRight()}</span>
      `;

      const content = document.createElement('div');
      content.className = 'addon-product-accordion__content';

      const contentInner = document.createElement('div');
      contentInner.className = 'addon-product-accordion__content-inner';

      if (firstVariant.description) {
        const description = document.createElement('div'); 
        description.className = 'addon-product-accordion__description';
        description.innerHTML = firstVariant.description; 
        contentInner.appendChild(description);
      }

      if (hasBespokeOptions) {
        const bespokeForm = this.renderBespokeForm(firstVariant.bespokeOptions, productId);
        contentInner.appendChild(bespokeForm);
      }

      const variantSelector = this.renderVariantSelector(variants, productId, hasBespokeOptions);
      contentInner.appendChild(variantSelector);
      content.appendChild(contentInner);

      /**
       * Toggle accordion open/close
       * Keep accordion open if it has selected variants
       */
      header.addEventListener('click', () => {
        const categorySelections = this.parentComponent?.selections?.get(categoryId);
        const hasSelection = variants.some(variant =>
          categorySelections?.has(variant.id)
        );

        const isCurrentlyOpen = productAccordion.classList.contains('is-open');

        if (isCurrentlyOpen && hasSelection) {
          return;
        }

        productAccordion.classList.toggle('is-open');
      });

      productAccordion.appendChild(header);
      productAccordion.appendChild(content);
      productsContainer.appendChild(productAccordion);

      if (hasBespokeOptions) {
        this.restoreBespokeFromSelection(productId, variants, categoryId);
      }
    });
  }

  /**
   * Groups variants by product ID
   * @param {Array} variants - Flat array of variant data
   * @returns {Map} Map<productId, Array<variantData>>
   */
  groupVariantsByProduct(variants) {
    const groups = new Map();
    variants.forEach(variant => {
      if (!groups.has(variant.productId)) {
        groups.set(variant.productId, []);
      }
      groups.get(variant.productId).push(variant);
    });
    return groups;
  }

  /**
   * Checks if product has any bespoke options
   * @param {Object} bespokeOptions - Bespoke options object
   * @returns {Boolean}
   */
  hasBespokeOptions(bespokeOptions) {
    if (!bespokeOptions) return false;
    return (bespokeOptions.dropdown && bespokeOptions.dropdown.length > 0)
        || (bespokeOptions.dimensions && bespokeOptions.dimensions.length > 0)
        || bespokeOptions.confirmation;
  }

  /**
   * Renders variant selector for all products
   * @param {Array} variants - Array of variant data
   * @param {String} productId - Product ID
   * @param {Boolean} hasBespokeOptions - Whether product has bespoke options
   * @returns {HTMLElement} Variant selector element
   */
  renderVariantSelector(variants, productId, hasBespokeOptions) {
    const container = document.createElement('div');
    container.className = 'addon-variant-list';

    variants.forEach((variant) => {
      const row = document.createElement('div');
      row.className = 'addon-variant-row';
      row.dataset.variantId = variant.id;

      const info = document.createElement('div');
      info.className = 'addon-variant-row__info';

      const title = document.createElement('div');
      title.className = 'addon-variant-row__title';
      title.textContent = variant.variantTitle;
      info.appendChild(title);

      const actions = document.createElement('div');
      actions.className = 'addon-variant-row__actions';

      const bespokeSelections = hasBespokeOptions ? this.getBespokeSelections(productId) : {};
      const calculatedPrice = this.calculatePrice(variant, bespokeSelections);
      const price = document.createElement('div');
      price.className = 'addon-variant-row__price';
      price.textContent = this.formatPriceDisplay(calculatedPrice);
      price.dataset.priceDisplay = '';
      price.dataset.baseVariantPrice = variant.price;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'addon-variant-row__add-btn button';
      button.dataset.variantId = variant.id;
      button.innerHTML = `${icons.basketPlus()}<span>Add</span>`;

      const categoryId = this.currentCategoryId;
      const isSelected = this.parentComponent?.selections?.get(categoryId)?.has(variant.id);
      if (isSelected) {
        row.classList.add('is-selected');
        button.classList.add('button--success');
        button.innerHTML = `${icons.basketPlus()}<span>Added</span>`;
      }

      /**
       * Check if category is at limit and disable unselected buttons
       */
      if (this.parentComponent) {
        const totalQuantity = this.parentComponent.getTotalQuantityForCategory(categoryId);

        const requiredQuantity = this.parentComponent.mainQuantity;
        const categoryEl = this.parentComponent.querySelector(`[data-category-id="${categoryId}"]`);
        const isRequired = categoryEl?.dataset.required === 'true';

        if (isRequired && totalQuantity >= requiredQuantity && !isSelected) {
          button.disabled = true;
          button.classList.add('button--disabled');
        }
      }

      if (hasBespokeOptions) {
        const bespokeValid = this.validateBespokeForm(productId);
        if (!bespokeValid && !isSelected) {
          button.disabled = true;
          button.classList.add('button--disabled');
        }
      }

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();

        if (hasBespokeOptions) {
          this.addBespokeVariant(productId, variant, row, button);
        } else {
          this.addVariantDirectly(productId, variant, row, button);
        }
      });

      actions.appendChild(price);
      actions.appendChild(button);

      row.appendChild(info);
      row.appendChild(actions);
      container.appendChild(row);
    });

    return container;
  }

  /**
   * Resets all variant buttons in a product to default state
   * @param {HTMLElement} variantList - Variant list container
   */
  resetVariantButtons(variantList) {
    const allRows = variantList.querySelectorAll('.addon-variant-row');
    const allButtons = variantList.querySelectorAll('.addon-variant-row__add-btn');

    allRows.forEach(r => r.classList.remove('is-selected'));
    allButtons.forEach(btn => {
      btn.classList.remove('button--success');
      btn.innerHTML = `${icons.basketPlus()}<span>Add</span>`;
    });
  }

  /**
   * Updates variant row and button visual state
   * @param {HTMLElement} row - Variant row element
   * @param {HTMLElement} button - Add button element
   * @param {Boolean} isSelected - Whether variant is selected
   */
  updateVariantVisualState(row, button, isSelected) {
    if (isSelected) {
      row.classList.add('is-selected');
      button.classList.add('button--success');
      button.innerHTML = `${icons.basketPlus()}<span>Added</span>`;
    }
  }

  /**
   * Adds a variant for a bespoke product
   * Validates bespoke form, collects selections, and commits directly
   * Supports toggling: click again to deselect
   * @param {String} productId - Product ID
   * @param {Object} variantData - Variant data
   * @param {HTMLElement} row - Variant row element
   * @param {HTMLElement} button - Add button element
   */
  addBespokeVariant(productId, variantData, row, button) {
    const accordion = this.querySelector(`[data-product-id="${productId}"]`);
    if (!accordion) return;

    const variantList = accordion.querySelector('.addon-variant-list');
    if (!variantList) return;

    const isCurrentlySelected = row.classList.contains('is-selected');
    const categoryId = this.currentCategoryId;

    this.resetVariantButtons(variantList);

    if (!isCurrentlySelected) {
      const isValid = this.validateBespokeForm(productId);
      if (!isValid) return;

      this.updateVariantVisualState(row, button, true);
      accordion.dataset.selectedVariantId = variantData.id;

      const bespokeSelections = this.getBespokeSelections(productId);

      /**
       * Calculate total price including bespoke adjustments
       * Pass calculated price to parent for accurate display and storage
       * Store total adjustment (dropdowns + dimensions) for _priceAdjustment cart property
       */
      const calculatedPrice = this.calculatePrice(variantData, bespokeSelections);
      bespokeSelections.totalPriceAdjustment = calculatedPrice - (variantData.price || 0);
      const variantWithPrice = {
        ...variantData,
        price: calculatedPrice
      };

      this.handleProductAdd(variantWithPrice, categoryId, bespokeSelections);
    } else {
      this.removeVariantSelection(accordion, variantData, categoryId);
    }

    this.updateDrawerCounter();
    this.updateCategoryLimitButtons(categoryId);
  }

  /**
   * Updates variant Add button disabled state based on category quantity limits
   * Used instead of full re-render to preserve bespoke form state
   * @param {String} categoryId - Category ID
   */
  updateCategoryLimitButtons(categoryId) {
    if (!this.parentComponent) return;

    const totalQuantity = this.parentComponent.getTotalQuantityForCategory(categoryId);
    const requiredQuantity = this.parentComponent.mainQuantity;
    const categoryEl = this.parentComponent.querySelector(`[data-category-id="${categoryId}"]`);
    const isRequired = categoryEl?.dataset.required === 'true';

    const allRows = this.querySelectorAll('.addon-variant-row');
    allRows.forEach(row => {
      const btn = row.querySelector('.addon-variant-row__add-btn');
      if (!btn) return;

      const isSelected = row.classList.contains('is-selected');
      if (isSelected) return;

      if (isRequired && totalQuantity >= requiredQuantity) {
        btn.disabled = true;
        btn.classList.add('button--disabled');
      }
    });
  }

  /**
   * Restores bespoke form values and variant selection from stored selections
   * Called after rendering a product accordion that has saved bespoke data
   * @param {String} productId - Product ID
   * @param {Array} variants - Array of variant data objects
   * @param {String} categoryId - Category ID
   */
  restoreBespokeFromSelection(productId, variants, categoryId) {
    if (!this.parentComponent) return;

    const categorySelections = this.parentComponent.selections.get(categoryId);
    if (!categorySelections) return;

    let selectedVariantId = null;
    let storedBespoke = null;

    variants.forEach(variant => {
      const sel = categorySelections.get(variant.id);
      if (sel?.bespokeOptions) {
        selectedVariantId = variant.id;
        storedBespoke = sel.bespokeOptions;
      }
    });

    if (!selectedVariantId || !storedBespoke) return;

    const accordion = this.querySelector(`[data-product-id="${productId}"]`);
    if (!accordion) return;

    const form = accordion.querySelector('.product-bespoke-options__form');
    if (!form) return;

    const isStructured = storedBespoke.dropdowns || storedBespoke.dimensions || storedBespoke.confirmation !== undefined;

    if (isStructured) {
      this.restoreBespokeStructured(form, storedBespoke);
    } else {
      this.restoreBespokeFromProperties(form, storedBespoke);
    }

    const row = accordion.querySelector(`.addon-variant-row[data-variant-id="${selectedVariantId}"]`);
    const button = row?.querySelector('.addon-variant-row__add-btn');
    if (row && button) {
      this.updateVariantVisualState(row, button, true);
      accordion.dataset.selectedVariantId = selectedVariantId;
    }

    this.handleBespokeChange(productId);
  }

  /**
   * Restores bespoke form from structured selections (from getBespokeSelections)
   * @param {HTMLElement} form - Bespoke form element
   * @param {Object} bespoke - Structured bespoke data with dropdowns/dimensions/confirmation
   */
  restoreBespokeStructured(form, bespoke) {
    if (bespoke.dropdowns) {
      Object.entries(bespoke.dropdowns).forEach(([dropdownId, data]) => {
        const select = form.querySelector(`.product-bespoke-dropdown__select[data-dropdown-id="${dropdownId}"]`);
        if (!select) return;

        for (let i = 0; i < select.options.length; i++) {
          if (select.options[i].dataset.label === data.value) {
            select.selectedIndex = i;
            select.classList.add('has-value');
            break;
          }
        }
      });
    }

    if (bespoke.dimensions) {
      bespoke.dimensions.forEach(dim => {
        const container = form.querySelector(`.product-bespoke-dimension[data-dimension-id="${dim.id}"]`);
        if (!container) return;

        if (dim.dim1) {
          const input1 = container.querySelector('.product-bespoke-dimension__input[data-slot="dim1"]');
          if (input1) input1.value = dim.dim1;
        }
        if (dim.dim2) {
          const input2 = container.querySelector('.product-bespoke-dimension__input[data-slot="dim2"]');
          if (input2) input2.value = dim.dim2;
        }
      });
    }

    if (bespoke.confirmation) {
      const checkbox = form.querySelector('.product-bespoke-confirmation__input');
      if (checkbox) {
        checkbox.setAttribute('aria-checked', 'true');
        checkbox.classList.add('product-bespoke-confirmation__input--checked');
      }
    }

    refreshCustomDropdowns(form, form._dropdownConfigs);
  }

  /**
   * Restores bespoke form from flat cart properties (from edit bundle)
   * Matches dropdown labels and dimension keys to form elements
   * @param {HTMLElement} form - Bespoke form element
   * @param {Object} properties - Flat key-value cart properties
   */
  restoreBespokeFromProperties(form, properties) {
    const selects = form.querySelectorAll('.product-bespoke-dropdown__select');
    selects.forEach(select => {
      const dropdownLabel = select.dataset.label;
      const savedValue = properties[dropdownLabel];
      if (!savedValue) return;

      for (let i = 0; i < select.options.length; i++) {
        if (select.options[i].dataset.label === savedValue) {
          select.selectedIndex = i;
          select.classList.add('has-value');
          break;
        }
      }
    });

    const dimInputs = form.querySelectorAll('.product-bespoke-dimension__input');
    dimInputs.forEach(input => {
      const dimLabel = input.dataset.label;
      const fieldLabel = input.dataset.fieldLabel;
      const key = `${dimLabel} ${fieldLabel} (mm)`;
      if (properties[key]) {
        input.value = properties[key];
      }
    });

    const checkbox = form.querySelector('.product-bespoke-confirmation__input');
    if (checkbox) {
      const confirmContainer = checkbox.closest('.product-bespoke-confirmation');
      if (!confirmContainer) return;

      const label = confirmContainer.querySelector('.product-bespoke-confirmation__label span');
      if (label && properties[label.textContent] === 'Yes') {
        checkbox.setAttribute('aria-checked', 'true');
        checkbox.classList.add('product-bespoke-confirmation__input--checked');
      }
    }

    refreshCustomDropdowns(form, form._dropdownConfigs);
  }

  /**
   * Removes variant from selections and dispatches event
   * @param {HTMLElement} accordion - Accordion element
   * @param {Object} variantData - Variant data
   * @param {String} categoryId - Category ID
   */
  removeVariantSelection(accordion, variantData, categoryId) {
    delete accordion.dataset.selectedVariantId;

    if (this.parentComponent) {
      const categorySelections = this.parentComponent.selections.get(categoryId);
      if (categorySelections) {
        categorySelections.delete(variantData.id);
        this.parentComponent.updateCategoryDisplay(categoryId);
        this.parentComponent.saveSelections();
        this.parentComponent.checkCompatibility();
        this.parentComponent.validateRequiredAddons();

        this.parentComponent.dispatchEvent(new CustomEvent('addon:deselected', {
          detail: { categoryId, variantId: variantData.id },
          bubbles: true
        }));

        this.updateDrawerCounter();
      }
    }
  }

  /**
   * Schedules product re-render on next frame
   * @param {String} categoryId - Category ID
   */
  scheduleProductsRerender(categoryId) {
    requestAnimationFrame(() => {
      const category = this.categoriesData?.categories?.find(c => c.id === categoryId)
        || this.legacyData?.legacyCategories?.find(c => c.id === categoryId);
      if (category) {
        this.renderProducts(category.products, categoryId);
      }
    });
  }

  /**
   * Adds variant directly for products without bespoke options
   * Supports toggling: click again to deselect, click different variant to switch
   * @param {String} productId - Product ID
   * @param {Object} variantData - Variant data
   * @param {HTMLElement} row - Variant row element
   * @param {HTMLElement} button - Add button element
   */
  addVariantDirectly(productId, variantData, row, button) {
    const accordion = this.querySelector(`[data-product-id="${productId}"]`);
    if (!accordion) return;

    const variantList = accordion.querySelector('.addon-variant-list');
    if (!variantList) return;

    const isCurrentlySelected = row.classList.contains('is-selected');
    const categoryId = this.currentCategoryId;

    this.resetVariantButtons(variantList);

    if (!isCurrentlySelected) {
      this.updateVariantVisualState(row, button, true);
      accordion.dataset.selectedVariantId = variantData.id;
      this.handleProductAdd(variantData, categoryId);
    } else {
      this.removeVariantSelection(accordion, variantData, categoryId);
    }

    this.updateDrawerCounter();
    this.scheduleProductsRerender(categoryId);
  }

  /**
   * Renders bespoke options form
   * @param {Object} bespokeOptions - Bespoke options data
   * @param {String} productId - Product ID
   * @returns {HTMLElement} Form element
   */
  renderBespokeForm(bespokeOptions, productId) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product-bespoke-options';

    const form = document.createElement('div');
    form.className = 'product-bespoke-options__form';
    form.dataset.productId = productId;

    if (bespokeOptions.dimensions && bespokeOptions.dimensions.length > 0) {
      bespokeOptions.dimensions.forEach(dimension => {
        form.appendChild(this.renderDimension(dimension, productId));
      });
    }

    if (bespokeOptions.dropdown && bespokeOptions.dropdown.length > 0) {
      form._dropdownConfigs = bespokeOptions.dropdown;
      bespokeOptions.dropdown.forEach(dropdown => {
        form.appendChild(this.renderDropdown(dropdown, productId));
      });
    }

    if (bespokeOptions.confirmation) {
      form.appendChild(this.renderConfirmation(bespokeOptions.confirmation, productId));
    }

    wrapper.appendChild(form);
    return wrapper;
  }

  /**
   * Renders a dropdown bespoke option
   * @param {Object} dropdown - Dropdown data
   * @param {String} productId - Product ID
   * @returns {HTMLElement}
   */
  renderDropdown(dropdown, productId) {
    const result = createBespokeDropdown(dropdown, {
      formatMoney: (cents) => this.formatMoney(cents),
      renderTooltip: (helpText) => this.renderBespokeTooltip(helpText)
    });

    result.select.addEventListener('change', () => {
      this.handleBespokeChange(productId);
    });

    return result.container;
  }

  /**
   * Renders dimension input(s)
   * @param {Object} dimension - Dimension data
   * @param {String} productId - Product ID
   * @returns {HTMLElement}
   */
  renderDimension(dimension, productId) {
    const container = document.createElement('div');
    container.className = 'product-bespoke-dimension';
    container.dataset.dimensionId = dimension.id;

    const labelRow = document.createElement('div');
    labelRow.className = 'product-bespoke-dimension__label-row';

    const label = document.createElement('span');
    label.className = 'product-bespoke-dimension__label';
    label.textContent = dimension.label;
    labelRow.appendChild(label);

    if (dimension.helpText) {
      labelRow.appendChild(this.renderBespokeTooltip(dimension.helpText));
    }

    container.appendChild(labelRow);

    const inputsRow = document.createElement('div');
    inputsRow.className = 'product-bespoke-dimension__inputs';

    inputsRow.appendChild(this.createDimensionField(
      dimension, 'dim1', dimension.label1, dimension.min1, dimension.max1, productId
    ));

    if (dimension.label2) {
      inputsRow.appendChild(this.createDimensionField(
        dimension, 'dim2', dimension.label2, dimension.min2, dimension.max2, productId
      ));
    }

    container.appendChild(inputsRow);

    return container;
  }

  /**
   * Creates a single dimension input field with unit label and range hint
   * @param {Object} dimension - Parent dimension config
   * @param {string} slot - 'dim1' or 'dim2'
   * @param {string} fieldLabel - Field label text
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @param {string} productId - Product ID for event binding
   * @returns {HTMLElement} Field container
   */
  createDimensionField(dimension, slot, fieldLabel, min, max, productId) {
    const field = document.createElement('div');
    field.className = 'product-bespoke-dimension__field';

    const inputWrap = document.createElement('div');
    inputWrap.className = 'product-bespoke-dimension__input-wrap';

    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'product-bespoke-dimension__input';
    input.dataset.dimensionId = dimension.id;
    input.dataset.slot = slot;
    input.dataset.label = dimension.label;
    input.dataset.fieldLabel = fieldLabel;
    input.dataset.minMm = min;
    input.dataset.maxMm = max;
    input.required = true;
    input.placeholder = fieldLabel;
    input.step = 'any';

    const imperial = isImperial();
    updateInputMinMax(input, min, max, imperial);

    input.addEventListener('input', () => this.handleBespokeChange(productId));
    input.addEventListener('blur', () => this.validateDimensionInput(input, productId));

    inputWrap.appendChild(input);

    const unit = document.createElement('span');
    unit.className = 'product-bespoke-dimension__unit';
    unit.textContent = imperial ? '"' : 'mm';
    inputWrap.appendChild(unit);

    const hint = document.createElement('span');
    hint.className = 'product-bespoke-dimension__range-hint';
    hint.dataset.minMm = min;
    hint.dataset.maxMm = max;
    hint.textContent = formatDimensionRange(min, max, imperial);
    inputWrap.appendChild(hint);

    field.appendChild(inputWrap);

    const errorMsg = document.createElement('span');
    errorMsg.className = 'product-bespoke-dimension__error';
    errorMsg.hidden = true;
    field.appendChild(errorMsg);

    return field;
  }

  /**
   * Renders confirmation checkbox
   * @param {Object} confirmation - Confirmation data
   * @param {String} productId - Product ID
   * @returns {HTMLElement}
   */
  renderConfirmation(confirmation, productId) {
    const container = document.createElement('div');
    container.className = 'product-bespoke-confirmation';
    container.dataset.confirmationId = confirmation.id;

    const row = document.createElement('div');
    row.className = 'product-bespoke-confirmation__row';

    const checkbox = document.createElement('button');
    checkbox.type = 'button';
    checkbox.className = 'product-bespoke-confirmation__input';
    checkbox.setAttribute('role', 'checkbox');
    checkbox.setAttribute('aria-checked', 'false');
    checkbox.innerHTML = icons.check();
    checkbox.addEventListener('click', (e) => {
      e.preventDefault();
      const isChecked = checkbox.getAttribute('aria-checked') === 'true';
      checkbox.setAttribute('aria-checked', String(!isChecked));
      checkbox.classList.toggle('product-bespoke-confirmation__input--checked', !isChecked);
      this.handleBespokeChange(productId);
    });

    const label = document.createElement('span');
    label.className = 'product-bespoke-confirmation__label';

    const labelText = document.createElement('span');
    labelText.textContent = confirmation.label;
    label.appendChild(labelText);

    if (confirmation.helpText) {
      label.appendChild(document.createTextNode(' '));
      label.appendChild(this.renderBespokeTooltip(confirmation.helpText));
    }

    row.appendChild(checkbox);
    row.appendChild(label);
    container.appendChild(row);

    return container;
  }

  /**
   * Renders a tooltip trigger button with popover content for addon bespoke forms
   * @param {string} helpText - HTML content for the tooltip
   * @returns {HTMLElement} Tooltip wrapper element
   */
  renderBespokeTooltip(helpText) {
    const wrap = document.createElement('span');
    wrap.className = 'product-bespoke-options__tooltip-wrap';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'product-bespoke-options__tooltip-trigger';
    trigger.innerHTML = icons.infoCircle();
    trigger.setAttribute('aria-label', 'More information');

    const tooltip = document.createElement('div');
    tooltip.className = 'product-bespoke-options__tooltip';

    const header = document.createElement('div');
    header.className = 'product-bespoke-options__tooltip-header';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'product-bespoke-options__tooltip-close';
    closeBtn.innerHTML = icons.close();
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      tooltip.classList.remove('product-bespoke-options__tooltip--open');
    });

    header.appendChild(closeBtn);

    const content = document.createElement('div');
    content.className = 'product-bespoke-options__tooltip-content';
    content.innerHTML = this.parseRichText(helpText);

    tooltip.appendChild(header);
    tooltip.appendChild(content);

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = tooltip.classList.contains('product-bespoke-options__tooltip--open');
      this.closeAllBespokeTooltips();
      if (!isOpen) tooltip.classList.add('product-bespoke-options__tooltip--open');
    });

    wrap.appendChild(trigger);
    wrap.appendChild(tooltip);

    return wrap;
  }

  /**
   * Closes all open bespoke tooltips within the addon drawer
   */
  closeAllBespokeTooltips() {
    const tooltips = this.querySelectorAll('.product-bespoke-options__tooltip');
    tooltips.forEach(t => { t.classList.remove('product-bespoke-options__tooltip--open'); });
  }

  /**
   * Validates a dimension input on blur and shows error state if out of range
   * @param {HTMLInputElement} input - The dimension input element
   * @param {string} productId - Product ID for triggering change
   */
  validateDimensionInput(input, productId) {
    this.touchedDimensions.add(input);

    const validation = getDimensionValidation(input);
    const data = parseDimensionData(input);

    const field = input.closest('.product-bespoke-dimension__field');
    const wrap = input.closest('.product-bespoke-dimension__input-wrap');
    const errorMsg = field?.querySelector('.product-bespoke-dimension__error');

    if (wrap) {
      wrap.classList.toggle('product-bespoke-dimension__input-wrap--error', validation.isOutOfRange);
    }

    if (errorMsg) {
      errorMsg.hidden = !validation.isOutOfRange;
      if (validation.isOutOfRange) {
        const translations = {
          minError: this.getTranslation('dimensionMinError', 'Min is'),
          maxError: this.getTranslation('dimensionMaxError', 'Max is'),
        };
        errorMsg.textContent = getDimensionErrorMessage(validation, data.minMm, data.maxMm, translations);
      }
    }

    this.handleBespokeChange(productId);
  }

  /**
   * Gets a translation string from the component's data attributes
   * @param {string} key - Translation key (camelCase)
   * @param {string} fallback - Fallback text
   * @returns {string} Translated text
   */
  getTranslation(key, fallback) {
    return this.dataset[`t${key.charAt(0).toUpperCase() + key.slice(1)}`] || fallback;
  }

  /**
   * Calculates total price including bespoke options
   * @param {Object} variantData - Variant data with base price
   * @param {Object} bespokeSelections - Selected bespoke options
   * @returns {Number} Total price in cents
   */
  calculatePrice(variantData, bespokeSelections) {
    let total = variantData.price || 0;

    if (bespokeSelections.dropdowns) {
      Object.values(bespokeSelections.dropdowns).forEach(dropdown => {
        const adjustment = parseInt(dropdown.priceAdjustment, 10) || 0;
        total += adjustment;
      });
    }

    if (bespokeSelections.dimensions && bespokeSelections.dimensions.length > 0 && variantData.bespokeOptions?.dimensions) {
      const dimensionDefs = variantData.bespokeOptions.dimensions;

      bespokeSelections.dimensions.forEach(sel => {
        const def = dimensionDefs.find(d => d.id === sel.id);
        if (!def) return;

        if (sel.dim1) {
          const value1 = parseInt(sel.dim1, 10) || 0;
          const min1 = parseInt(def.min1, 10) || 0;
          const pricePerMm1 = parseInt(def.pricePerMm1, 10) || 0;
          if (value1 > min1 && pricePerMm1 > 0) {
            total += (value1 - min1) * pricePerMm1;
          }
        }

        if (sel.dim2 && def.label2) {
          const value2 = parseInt(sel.dim2, 10) || 0;
          const min2 = parseInt(def.min2, 10) || 0;
          const pricePerMm2 = parseInt(def.pricePerMm2, 10) || 0;
          if (value2 > min2 && pricePerMm2 > 0) {
            total += (value2 - min2) * pricePerMm2;
          }
        }
      });
    }

    return total;
  }

  /**
   * Gets current bespoke selections for a product
   * @param {String} productId - Product ID
   * @returns {Object} Object with dropdowns and dimensions
   */
  getBespokeSelections(productId) {
    const accordion = this.querySelector(`[data-product-id="${productId}"]`);
    const form = accordion?.querySelector('.product-bespoke-options__form');
    if (!form) return {};

    const selections = {
      dropdowns: {},
      dimensions: [],
      confirmation: false
    };

    const dropdowns = form.querySelectorAll('.product-bespoke-dropdown__select');
    dropdowns.forEach(select => {
      if (select.value) {
        const selectedOption = select.options[select.selectedIndex];
        const priceAdjustment = parseInt(selectedOption.dataset.priceAdjustment) || 0;
        const dropdownLabel = select.closest('.product-bespoke-dropdown')?.querySelector('.product-bespoke-dropdown__label');
        selections.dropdowns[select.dataset.dropdownId] = {
          priceAdjustment,
          label: dropdownLabel?.textContent || select.dataset.dropdownId,
          value: selectedOption.dataset.label || selectedOption.textContent.replace(/\s*\(.*\)\s*$/, ''),
          swatch: selectedOption.dataset.swatch || null
        };
      }
    });

    const dimensionContainers = form.querySelectorAll('.product-bespoke-dimension[data-dimension-id]');
    const imperial = isImperial();
    dimensionContainers.forEach(container => {
      const dimensionId = container.dataset.dimensionId;
      const dimensionLabel = container.querySelector('.product-bespoke-dimension__label');
      const dim1Input = container.querySelector('.product-bespoke-dimension__input[data-slot="dim1"]');
      const dim2Input = container.querySelector('.product-bespoke-dimension__input[data-slot="dim2"]');
      const entry = {
        id: dimensionId,
        label: dimensionLabel?.textContent || dimensionId
      };
      if (dim1Input && dim1Input.value) {
        let mmValue = parseFloat(dim1Input.value);
        if (imperial) {
          mmValue = toMm(mmValue);
        }
        entry.dim1 = mmValue.toString();
        entry.label1 = dim1Input.dataset.fieldLabel || 'Dimension 1';
      }
      if (dim2Input && dim2Input.value) {
        let mmValue = parseFloat(dim2Input.value);
        if (imperial) {
          mmValue = toMm(mmValue);
        }
        entry.dim2 = mmValue.toString();
        entry.label2 = dim2Input.dataset.fieldLabel || 'Dimension 2';
      }
      if (entry.dim1 || entry.dim2) {
        selections.dimensions.push(entry);
      }
    });

    const confirmation = form.querySelector('.product-bespoke-confirmation__input');
    if (confirmation) {
      const isChecked = confirmation.getAttribute('aria-checked') === 'true';
      const confirmContainer = confirmation.closest('.product-bespoke-confirmation');
      const confirmLabel = confirmContainer?.querySelector('.product-bespoke-confirmation__label span');
      selections.confirmation = isChecked;
      selections.confirmationLabel = confirmLabel?.textContent || '';
    }

    return selections;
  }

/**
 * Handles bespoke option change
 * Updates all variant row prices and toggles Add button disabled state.
 * If a variant is already selected and the form is still valid, automatically
 * re-commits the selection with the updated bespoke values so the user does
 * not need to toggle the Add button manually.
 * @param {String} productId - Product ID
 */
  handleBespokeChange(productId) {
    const accordion = this.querySelector(`[data-product-id="${productId}"]`);
    if (!accordion) return;

    const variantList = accordion.querySelector('.addon-variant-list');
    if (!variantList) return;

    const categoryId = this.currentCategoryId;
    const category = this.categoriesData?.categories?.find(c => c.id === categoryId)
      || this.legacyData?.legacyCategories?.find(c => c.id === categoryId);
    if (!category) return;

    const bespokeSelections = this.getBespokeSelections(productId);
    const isValid = this.validateBespokeForm(productId);

    const productIdNum = parseInt(productId, 10);
    const productVariants = category.products.filter(v =>
      parseInt(v.productId, 10) === productIdNum
    );
    const bespokeOptions = productVariants[0]?.bespokeOptions || null;

    const rows = variantList.querySelectorAll('.addon-variant-row');
    if (rows.length === 0) return;

    rows.forEach(row => {
      const variantId = row.dataset.variantId;
      const variantIdNum = parseInt(variantId, 10);
      const variantData = category.products.find(v => parseInt(v.id, 10) === variantIdNum);
      if (!variantData) return;

      const variantWithBespoke = {
        ...variantData,
        bespokeOptions: variantData.bespokeOptions || bespokeOptions
      };

      const calculatedPrice = this.calculatePrice(variantWithBespoke, bespokeSelections);

      const priceEl = row.querySelector('[data-price-display]');
      if (priceEl) {
        priceEl.textContent = this.formatPriceDisplay(calculatedPrice);
      }

      const button = row.querySelector('.addon-variant-row__add-btn');
      const isSelected = row.classList.contains('is-selected');

      if (button && !isSelected) {
        button.disabled = !isValid;
        button.classList.toggle('button--disabled', !isValid);
      }

      if (isSelected && isValid && this.parentComponent) {
        const updatedBespoke = { ...bespokeSelections };
        updatedBespoke.totalPriceAdjustment = calculatedPrice - (variantData.price || 0);

        const variantWithPrice = { ...variantWithBespoke, price: calculatedPrice };

        this.parentComponent.handleProductSelection({
          categoryId,
          variantId: variantData.id,
          productData: variantWithPrice,
          bespokeOptions: updatedBespoke
        });
      }
    });
  }

  /**
   * Validates bespoke form completeness
   * @param {String} productId - Product ID
   * @returns {Boolean} True if all required bespoke fields are filled
   */
  validateBespokeForm(productId) {
    const accordion = this.querySelector(`[data-product-id="${productId}"]`);
    if (!accordion) return false;

    const form = accordion.querySelector('.product-bespoke-options__form');
    if (!form) return true;

    const dropdowns = form.querySelectorAll('.product-bespoke-dropdown__select');
    for (const select of dropdowns) {
      if (!select.value) return false;
    }

    const dimInputs = form.querySelectorAll('.product-bespoke-dimension__input');
    for (const input of dimInputs) {
      const value = parseFloat(input.value);
      if (!input.value || isNaN(value) || value < parseFloat(input.min) || value > parseFloat(input.max)) {
        return false;
      }
    }

    const checkbox = form.querySelector('.product-bespoke-confirmation__input');
    if (checkbox && checkbox.getAttribute('aria-checked') !== 'true') {
      return false;
    }

    return true;
  }

  /**
   * Handles product add button click
   * @param {Object} productData - Product data object
   * @param {String} categoryId - Category ID
   * @param {Object|null} bespokeSelections - Bespoke selections if applicable
   */
  handleProductAdd(productData, categoryId, bespokeSelections) {
    if (this.parentComponent) {
      this.parentComponent.handleProductSelection({
        categoryId,
        variantId: productData.id,
        productData,
        bespokeOptions: bespokeSelections || null
      });

      this.updateDrawerCounter();
    }
  }

  /**
   * Formats price in money format
   * @param {Number} cents - Price in cents
   * @returns {String} Formatted price
   */
  formatMoney(cents) {
    const pounds = (cents / 100).toFixed(2);
    return `£${pounds}`;
  }

  /**
   * Formats price for display, showing "Included" for zero prices
   * @param {Number} cents - Price in cents
   * @returns {String} Formatted price or "Included"
   */
  formatPriceDisplay(cents) {
    return cents === 0 ? 'Included' : this.formatMoney(cents);
  }
}

customElements.define('product-addons', ProductAddons);
customElements.define('product-addons-drawer', ProductAddonsDrawer);

})();
